import React, { useState, useEffect } from 'react';
import { tokens } from "../../theme";
import {
    Box, Typography, useTheme, Button, TextField, Select, MenuItem,
    FormControl, InputLabel, FormHelperText, IconButton, Paper, Chip, Divider,
    Switch, FormControlLabel, Checkbox, Accordion, AccordionSummary,
    AccordionDetails, CircularProgress, LinearProgress, Tooltip, Dialog, DialogTitle,
    DialogContent, DialogActions, Autocomplete, ToggleButtonGroup, ToggleButton,
    Tab, Tabs, Menu, Alert,
} from "@mui/material";
import Header from "../../components/Header";
import QuestionRender from "../../components/QuestionRender";
import useSignedContent from "../../utils/useSignedContent";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from '../../utils/v2Api';
import { amcTypeForTitle, amcScoringProfile } from './amcTitleAutofill';
import ImportQuestionsModal from './ImportQuestionsModal';
import GenerateFromChaptersModal from './GenerateFromChaptersModal';
import { contentKey, stripPreview, clusterByGroup } from './importUtils';
import { perAttemptOptions } from './vocabConfig';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import UnsavedChangesGuard from '../../components/UnsavedChangesGuard';
import ConfirmDialog from '../../components/ConfirmDialog';
import ManageableAutocomplete from '../../components/ManageableAutocomplete';
import AIAutoFillModal from './AIAutoFillModal';

// ─── Drop Zone Component ───────────────────────────────────────

// Tiny caption above a compact input — the answers rows pack many
// similar-looking boxes; this names each one (admin request).
const Labeled = ({ label, children }) => (
    <Box display="flex" flexDirection="column" alignItems="center" gap="2px">
        <Typography sx={{ fontSize: 9, lineHeight: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em' }}>
            {label}
        </Typography>
        {children}
    </Box>
);

const FileDropZone = ({ label, hint, uploadedUrl, uploadedName, uploading, progress, onFile, onClear, accept = ".pdf" }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const inputRef = React.useRef(null);
    const [dragOver, setDragOver] = React.useState(false);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const dropped = e.dataTransfer.files[0];
        if (dropped) onFile(dropped);
    };

    const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
    const handleDragLeave = () => setDragOver(false);

    return (
        <Box>
            {/* who sees this file + what it's for — hover the info icon (a visible
                caption wraps at different heights and misaligns the four zones) */}
            <Box display="flex" alignItems="center" gap={0.5} mb={0.5} sx={{ flexWrap: 'nowrap', minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap sx={{ minWidth: 0 }}>{label}</Typography>
                {hint && (
                    <Tooltip title={hint} arrow placement="top">
                        <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                    </Tooltip>
                )}
            </Box>
            <Box
                onClick={() => !uploading && inputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                sx={{
                    border: `2px dashed ${uploading ? colors.blueAccent[400] : dragOver ? colors.blueAccent[400] : colors.grey[500]}`,
                    borderRadius: 1,
                    p: 2,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    overflow: 'hidden',
                    cursor: uploading ? 'default' : 'pointer',
                    bgcolor: dragOver ? (theme.palette.mode === 'dark' ? 'rgba(104,112,250,0.08)' : 'rgba(104,112,250,0.05)') : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': !uploading ? {
                        borderColor: colors.blueAccent[400],
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(104,112,250,0.08)' : 'rgba(104,112,250,0.05)',
                    } : {},
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    hidden
                    accept={accept}
                    onChange={(e) => { if (e.target.files[0]) onFile(e.target.files[0]); }}
                />
                {uploading ? (
                    <Box sx={{ width: '100%', px: 1 }}>
                        <Typography variant="caption" color="text.secondary" mb={0.5} display="block">Uploading... {progress}%</Typography>
                        <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
                    </Box>
                ) : uploadedUrl ? (
                    <Box display="flex" alignItems="center" gap={1} sx={{ overflow: 'hidden', width: '100%', px: 1 }}>
                        <UploadFileIcon sx={{ fontSize: 22, color: 'success.main', flexShrink: 0 }} />
                        <Tooltip title={uploadedName || 'Uploaded'}><Typography variant="caption" noWrap sx={{ flex: 1 }}>{uploadedName || 'Uploaded — click to replace'}</Typography></Tooltip>
                        <Chip
                            label="View"
                            size="small"
                            component="a"
                            href={uploadedUrl}
                            target="_blank"
                            clickable
                            onClick={(e) => e.stopPropagation()}
                            sx={{ flexShrink: 0 }}
                        />
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClear(); }} sx={{ flexShrink: 0 }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                        <UploadFileIcon sx={{ fontSize: 28, color: colors.grey[500] }} />
                        <Typography variant="caption" color={colors.grey[500]}>
                            Drop file here or click to browse
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// ─── Lightweight input styles (replaces heavy MUI TextFields in question cards) ──
const liteInputSx = {
    border: '1px solid',
    borderColor: 'grey.600',
    borderRadius: 1,
    px: 1,
    py: 0.5,
    fontSize: 13,
    bgcolor: 'transparent',
    color: 'inherit',
    outline: 'none',
    fontFamily: 'inherit',
    '&:focus': { borderColor: 'primary.main' },
};
const liteSelectSx = {
    ...liteInputSx,
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23999' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 6px center',
    pr: 2.5,
};

// ─── Recursive Part Tree ────────────────────────────────────────

const LABEL_SCHEMES = {
    'a-z': (n) => String.fromCharCode(96 + n), // a, b, c...
    'A-Z': (n) => String.fromCharCode(64 + n), // A, B, C...
    'i': (n) => ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x', 'xi', 'xii'][n - 1] || String(n),
    'I': (n) => ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'][n - 1] || String(n),
    '1': (n) => String(n),
};

const SCHEME_OPTIONS = [
    { value: 'a-z', label: 'a, b, c' },
    { value: 'A-Z', label: 'A, B, C' },
    { value: 'i', label: 'i, ii, iii' },
    { value: 'I', label: 'I, II, III' },
    { value: '1', label: '1, 2, 3' },
];

// ─── AP Exam Format Reference ───────────────────────────────────

const AP_EXAM_FORMATS = {
    hybrid: [
        'AP Biology', 'AP Calculus AB', 'AP Calculus BC', 'AP Chemistry',
        'AP Macroeconomics', 'AP Microeconomics', 'AP Physics 1: Algebra-Based',
        'AP Physics 2: Algebra-Based', 'AP Physics C: Electricity and Magnetism',
        'AP Physics C: Mechanics', 'AP Precalculus', 'AP Statistics',
    ],
    digital: [
        'AP African American Studies', 'AP Art History', 'AP Comparative Government and Politics',
        'AP Computer Science A', 'AP Computer Science Principles', 'AP English Language and Composition',
        'AP English Literature and Composition', 'AP Environmental Science', 'AP European History',
        'AP Human Geography', 'AP Latin', 'AP Psychology', 'AP Seminar',
        'AP United States Government and Politics', 'AP United States History', 'AP World History: Modern',
    ],
};

function getApExamFormat(subjectName) {
    if (!subjectName) return null;
    const lower = subjectName.toLowerCase();
    if (AP_EXAM_FORMATS.hybrid.some(s => lower.includes(s.toLowerCase()) || s.toLowerCase().includes(lower))) return 'hybrid';
    return 'digital'; // default to text if not in hybrid list
}

function relabelParts(parts, scheme) {
    const gen = LABEL_SCHEMES[scheme] || LABEL_SCHEMES['a-z'];
    return parts.map((p, i) => ({ ...p, part_label: gen(i + 1) }));
}

const PartTree = ({ parts, onChange, depth = 0, scheme: parentScheme }) => {
    const [scheme, setScheme] = React.useState(parentScheme || (depth === 0 ? 'a-z' : depth === 1 ? 'i' : '1'));

    const updatePart = (idx, field, value) => {
        onChange(parts.map((p, i) => i === idx ? { ...p, [field]: value } : p));
    };

    const addPart = () => {
        const gen = LABEL_SCHEMES[scheme] || LABEL_SCHEMES['a-z'];
        const label = gen(parts.length + 1);
        onChange([...parts, { part_label: label, max_points: 1, subparts: [] }]);
    };

    const removePart = (idx) => {
        // Preserve manual label edits — don't re-run the auto scheme generator.
        // Admin can pick the scheme dropdown again if they want a clean renumber.
        const remaining = parts.filter((_, i) => i !== idx);
        onChange(remaining);
    };

    const handleSchemeChange = (newScheme) => {
        // Explicit scheme change — admin opted into renumbering everything.
        setScheme(newScheme);
        onChange(relabelParts(parts, newScheme));
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const { source, destination } = result;
        if (source.index === destination.index) return;
        const reordered = [...parts];
        const [moved] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, moved);
        // Preserve labels — drag is reordering, not renumbering.
        onChange(reordered);
    };

    const droppableId = `parts-${depth}-${Math.random().toString(36).slice(2, 8)}`;

    return (
        <Box ml={depth * 3}>
            {parts.length > 0 && (
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="caption" color="text.secondary">Label:</Typography>
                    <Select
                        size="small"
                        value={scheme}
                        onChange={(e) => handleSchemeChange(e.target.value)}
                        sx={{ height: 24, fontSize: 12, '& .MuiSelect-select': { py: 0, px: 1 } }}
                    >
                        {SCHEME_OPTIONS.map(s => <MenuItem key={s.value} value={s.value} sx={{ fontSize: 12 }}>{s.label}</MenuItem>)}
                    </Select>
                </Box>
            )}
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId={droppableId}>
                    {(provided) => (
                        <Box ref={provided.innerRef} {...provided.droppableProps}>
                            {parts.map((part, idx) => (
                                <Draggable key={`part-${depth}-${idx}`} draggableId={`part-${depth}-${idx}`} index={idx}>
                                    {(prov, snap) => (
                                        <Box ref={prov.innerRef} {...prov.draggableProps} mb={0.5} sx={{ opacity: snap.isDragging ? 0.7 : 1 }}>
                                            <Box display="flex" gap={0.5} alignItems="center">
                                                <Box {...prov.dragHandleProps} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
                                                    <Typography sx={{ fontSize: 14, lineHeight: 1, userSelect: 'none' }}>⠿</Typography>
                                                </Box>
                                                <TextField
                                                    size="small"
                                                    value={part.part_label}
                                                    onChange={(e) => updatePart(idx, 'part_label', e.target.value)}
                                                    sx={{ width: 60 }}
                                                    inputProps={{ style: { fontWeight: 600, fontSize: 13 } }}
                                                />
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={part.max_points}
                                                    onChange={(e) => updatePart(idx, 'max_points', parseInt(e.target.value) || 0)}
                                                    sx={{ width: 80, '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' }, '& input[type=number]': { MozAppearance: 'textfield' } }}
                                                    inputProps={{ min: 0, style: { fontSize: 13 } }}
                                                    InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">pt</Typography> }}
                                                />
                                                <IconButton size="small" onClick={() => {
                                                    const subs = part.subparts || [];
                                                    const childScheme = depth === 0 ? 'i' : depth === 1 ? '1' : 'a-z';
                                                    const gen = LABEL_SCHEMES[childScheme];
                                                    const label = gen(subs.length + 1);
                                                    updatePart(idx, 'subparts', [...subs, { part_label: label, max_points: 1, subparts: [] }]);
                                                }}>
                                                    <AddIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => removePart(idx)}>
                                                    <DeleteIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Box>
                                            {part.subparts?.length > 0 && (
                                                <PartTree
                                                    parts={part.subparts}
                                                    onChange={(newSubs) => updatePart(idx, 'subparts', newSubs)}
                                                    depth={depth + 1}
                                                />
                                            )}
                                        </Box>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>
            <Button size="small" startIcon={<AddIcon />} onClick={addPart} sx={{ mt: 0.5 }}>
                {depth === 0 ? 'Add Subquestion' : 'Add'}
            </Button>
        </Box>
    );
};

// ─── Preview Parts (recursive) ──────────────────────────────────

const PreviewParts = ({ parts, questionType, depth = 0, colors, isUpload }) => (
    <Box ml={depth + 1} mt={depth === 0 ? 0.5 : 0}>
        {parts.map((p, i) => (
            <Box key={i} sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.3 }}>
                    ({p.part_label}) <Typography component="span" sx={{ fontSize: 10, color: 'text.secondary' }}>{p.max_points}pt{p.max_points != 1 ? 's' : ''}</Typography>
                </Typography>
                {(!p.subparts || p.subparts.length === 0) && (
                    isUpload ? (
                        <Box sx={{ p: 0.5, border: `1px dashed ${colors.grey[500]}`, borderRadius: 0.5, minHeight: 24, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: 9, color: 'text.secondary' }}>📎</Typography>
                            <Typography sx={{ fontSize: 9, color: 'text.secondary', fontStyle: 'italic' }}>Upload file...</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ p: 0.5, border: `1px dashed ${colors.grey[500]}`, borderRadius: 0.5, minHeight: 16 }}>
                            <Typography sx={{ fontSize: 9, color: 'text.secondary', fontStyle: 'italic' }}>
                                {questionType === 'saq' ? 'Short answer...' : 'Essay response...'}
                            </Typography>
                        </Box>
                    )
                )}
                {p.subparts?.length > 0 && (
                    <PreviewParts parts={p.subparts} questionType={questionType} depth={depth + 1} colors={colors} isUpload={isUpload} />
                )}
            </Box>
        ))}
    </Box>
);

const QUESTION_TYPES = [
    { value: 'mcq', label: 'Multiple Choice' },
    { value: 'multi_answer', label: 'Short Answer (text)' },
    { value: 'grid_in', label: 'Grid-in (numeric)' },
    { value: 'saq', label: 'SAQ (Short Answer Question)' },
    { value: 'dbq', label: 'DBQ (Document-Based)' },
    { value: 'leq', label: 'LEQ (Long Essay)' },
];

const SECTION_TYPES = [
    { value: 'mcq', label: 'Multiple Choice' },
    { value: 'multi_answer', label: 'Multi-Answer' },
    { value: 'saq', label: 'SAQ' },
    { value: 'dbq', label: 'DBQ' },
    { value: 'leq', label: 'LEQ' },
    { value: 'mixed', label: 'Mixed' },
];

const DEFAULT_OPTIONS = ['A', 'B', 'C', 'D'];

// Normalize sections for save payload AND for dirty-comparison.
// Applies the same parseInt / JSON.parse transformations the save path uses,
// so the initial snapshot and the save-time value match when nothing changed.
const STRIP_TEMP_KEYS = (k, v) => k.startsWith('_') ? undefined : v;
// Chapters are per-question: each question carries its own course context
// (_chapter_course/_chapter_kind, set on load from its linked chapter, or by the
// editor when the admin first tags it). Untouched numbers keep their exact
// chapter row; edited numbers re-resolve as (course, kind, number); clearing an
// existing tag sends chapter_id: null; questions never tagged say nothing.
function buildParsedSections(sections) {
    return (sections || []).map(s => {
        const types = new Set((s.questions || []).map(q => q.question_type));
        const section_type = types.size === 1 ? types.values().next().value : 'mixed';
        return {
            ...s,
            section_type,
            time_limit_minutes: s.time_limit_minutes ? parseInt(s.time_limit_minutes) : null,
            questions: (s.questions || []).map(({ chapter_number, chapter_id: loadedChapterId, _loaded_chapter_number, _chapter_course, _chapter_kind, _chapter_label, ...q }) => ({
                ...q,
                ...(chapter_number === _loaded_chapter_number && loadedChapterId
                    ? { chapter_id: loadedChapterId }
                    : (chapter_number !== '' && chapter_number != null
                        ? (_chapter_course
                            // kind passes through verbatim ('difficulty' included) — coercing
                            // it would silently re-tag edited questions into a wrong chapter
                            ? { chapter: { course: _chapter_course, kind: _chapter_kind || 'chapter', number: parseInt(chapter_number) } }
                            : {})
                        : (loadedChapterId ? { chapter_id: null } : {}))),
                max_points: q.max_points ? parseInt(q.max_points) : 1,
                accepted_answers: (() => {
                    if (!q.accepted_answers) return null;
                    if (typeof q.accepted_answers === 'string') {
                        try { return JSON.parse(q.accepted_answers); } catch { return null; }
                    }
                    return q.accepted_answers;
                })(),
                parts: (q.parts || []).map(p => ({
                    ...p,
                    max_points: parseInt(p.max_points) || 1,
                    rubric_template_id: p.rubric_template_id || null,
                })),
            })),
        };
    });
}

// Generate `count` sequential A-Z labels starting from `startLetter`.
// Returns null if startLetter isn't a valid A-Z character.
function generateLabelsFromStart(startLetter, count) {
    if (!startLetter || typeof startLetter !== 'string' || startLetter.length === 0) return null;
    const startCode = startLetter.toUpperCase().charCodeAt(0);
    if (startCode < 65 || startCode > 90) return null;
    return Array.from({ length: count }, (_, i) => String.fromCharCode(startCode + i));
}

// Find duplicate option_label values within a question's options.
// Returns the unique duplicate strings (each one only listed once even if it
// appears 3+ times). Empty/whitespace labels are ignored.
function findDuplicateLabels(options) {
    const seen = new Set();
    const dups = new Set();
    for (const opt of options || []) {
        const label = (opt.option_label || '').trim();
        if (!label) continue;
        if (seen.has(label)) dups.add(label);
        seen.add(label);
    }
    return Array.from(dups);
}

// Calculate total max score for a section's questions, accounting for:
// - MCQ: each worth q.max_points (default 1)
// - FRQ: sum of leaf parts' max_points (recursive through subparts)
// - Choice groups: only the highest-scoring question in each group counts
const MCQ_TYPES = ['mcq', 'multi_answer', 'grid_in', 'sa'];
function calculateSectionScore(questions) {
    let total = 0;
    const choiceGroupMax = {};

    const getLeafSum = (parts) => {
        let sum = 0;
        for (const p of parts || []) {
            if (p.subparts && p.subparts.length > 0) {
                sum += getLeafSum(p.subparts);
            } else {
                sum += parseFloat(p.max_points) || 0;
            }
        }
        return sum;
    };

    for (const q of questions || []) {
        let qPoints;
        if (MCQ_TYPES.includes(q.question_type)) {
            qPoints = parseFloat(q.max_points) || 1;
        } else {
            qPoints = (q.parts && q.parts.length > 0)
                ? getLeafSum(q.parts)
                : (parseFloat(q.max_points) || 0);
        }

        if (q.choice_group) {
            const key = String(q.choice_group);
            choiceGroupMax[key] = Math.max(choiceGroupMax[key] || 0, qPoints);
        } else {
            total += qPoints;
        }
    }

    for (const g of Object.keys(choiceGroupMax)) {
        total += choiceGroupMax[g];
    }

    return total;
}

/** QuestionRender wrapper that signs S3 URLs before rendering */
const SignedQuestionRender = ({ content, sx }) => {
    const signed = useSignedContent(content);
    return <QuestionRender content={signed} sx={sx} />;
};

/**
 * Memoized read-only preview pane for one question. The KaTeX/HTML render inside
 * SignedQuestionRender is the expensive part, so this only re-renders when its own
 * question object reference changes (i.e. that specific row was edited) — editing a
 * different row no longer reconciles every preview on screen.
 */
const QuestionPreviewPane = React.memo(function QuestionPreviewPane({ q, bg }) {
    return (
        <Box sx={{ flex: 1, minWidth: 0, p: 2, bgcolor: bg, borderRadius: 1, overflow: 'auto', maxHeight: 400 }}>
            <Typography variant="caption" color="text.secondary" mb={1} display="block">Preview</Typography>
            {q.question_prompt ? (
                <SignedQuestionRender content={q.question_prompt} sx={{ fontSize: '0.9rem', lineHeight: 1.6, mb: 1 }} />
            ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">No prompt yet</Typography>
            )}
            {q.question_type === 'mcq' && q.options?.map((opt, oIdx) => (
                <Box key={oIdx} display="flex" gap={1} mb={0.5}>
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 20 }}>{opt.option_label}.</Typography>
                    {opt.option_text ? (
                        <SignedQuestionRender content={opt.option_text} sx={{ fontSize: '0.85rem' }} />
                    ) : (
                        <Typography variant="body2" color="text.secondary">{opt.option_label}</Typography>
                    )}
                </Box>
            ))}
        </Box>
    );
});

const AddEditTest = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const isEdit = Boolean(id);
    const isDuplicate = Boolean(searchParams.get('duplicate'));

    const [loading, setLoading] = useState(isEdit || isDuplicate);
    const [saving, setSaving] = useState(false);
    const [folders, setFolders] = useState([]);
    const [scoringProfiles, setScoringProfiles] = useState([]);
    const [rubricTemplates, setRubricTemplates] = useState([]);

    // Test form state
    const [title, setTitle] = useState('');
    const [folderId, setFolderId] = useState(null);
    const [scoringProfileId, setScoringProfileId] = useState('');
    const [subjectId, setSubjectId] = useState('');
    const [testTypeId, setTestTypeId] = useState('');
    const [testCategory, setTestCategory] = useState('regular');
    // Chapter tagging is per-question. When the admin types a number on an
    // untagged question, the course comes from the question itself, then from
    // the test's other tagged questions, then from a one-time dialog whose
    // answer is remembered for the session.
    const [chapterCourses, setChapterCourses] = useState([]); // options for the dialog
    const [sessionChapterCourse, setSessionChapterCourse] = useState(null); // {course, kind}
    const [courseDialog, setCourseDialog] = useState(null); // pending {sIdx, qIdx, value}
    const [dialogCourse, setDialogCourse] = useState('');
    const [dialogKind, setDialogKind] = useState('chapter');
    // Import Questions picker: 'new' = a brand-new section, number = existing
    // section index, null = closed. Target is chosen from the button's menu.
    const [importTarget, setImportTarget] = useState(null);
    const [importMenuAnchor, setImportMenuAnchor] = useState(null);
    const [generateOpen, setGenerateOpen] = useState(false);
    const [testTypes, setTestTypes] = useState([]);
    const [addTestTypeOpen, setAddTestTypeOpen] = useState(false);
    const [newTestTypeName, setNewTestTypeName] = useState('');
    const [subjects, setSubjects] = useState([]);
    const [sections, setSections] = useState([]);
    const [initialSectionsJson, setInitialSectionsJson] = useState(null);
    const [initialScoringProfileId, setInitialScoringProfileId] = useState(null);
    // Per-question start-letter input for the MCQ "shift labels from X" shortcut.
    // Key format: `${sIdx}-${qIdx}` → string (uppercase, single char). Not persisted.
    const [startLettersByQuestion, setStartLettersByQuestion] = useState({});
    const [newSubjectName, setNewSubjectName] = useState('');
    const [addSubjectOpen, setAddSubjectOpen] = useState(false);
    const [addProfileOpen, setAddProfileOpen] = useState(false);
    const [editingProfileId, setEditingProfileId] = useState(null);
    const [newProfileName, setNewProfileName] = useState('');
    const [newProfileSubject, setNewProfileSubject] = useState('');
    const [newProfileMcqMultiplier, setNewProfileMcqMultiplier] = useState('');
    const [newProfileFrqDefaultMultiplier, setNewProfileFrqDefaultMultiplier] = useState('');
    const [newProfileFrqMults, setNewProfileFrqMults] = useState([]); // [{ question, type, multiplier, max_points, label }]
    const [newProfileFrqTargets, setNewProfileFrqTargets] = useState(''); // comma-separated target points, e.g. "10,12,10,8"
    const [recomputeDialogOpen, setRecomputeDialogOpen] = useState(false);
    const [recomputeProfileId, setRecomputeProfileId] = useState(null);
    const [recomputing, setRecomputing] = useState(false);
    const [recomputeNavigateAfter, setRecomputeNavigateAfter] = useState(false);
    const [newProfileConversion, setNewProfileConversion] = useState([
        { score: '5', min: '', max: '' },
        { score: '4', min: '', max: '' },
        { score: '3', min: '', max: '' },
        { score: '2', min: '', max: '' },
        { score: '1', min: '', max: '' },
    ]);

    // Title-driven autofill (AMC): "AMC 10 …" / "AMC 12 …" files the test under that type and
    // attaches the AMC scoring profile. Fills only a field that is still EMPTY or that this
    // autofill itself set — a type or profile the admin picked by hand is never overwritten, so
    // an "AMC 12 …" title can still be filed elsewhere on purpose. Applied wherever the admin
    // sets the title: typing it, or the PDF-filename prefill. Loading an existing test does not
    // go through here, so opening an old test never changes it.
    const autoFilled = React.useRef({ typeId: null, profileId: null });
    const applyTitle = (next) => {
        setTitle(next);
        const type = amcTypeForTitle(next, testTypes);
        const typeIsOurs = testTypeId === '' || testTypeId === autoFilled.current.typeId;
        const profileIsOurs = scoringProfileId === '' || scoringProfileId === autoFilled.current.profileId;
        if (!type) {
            // Title no longer names an AMC paper: take back what the autofill set, nothing else.
            if (autoFilled.current.typeId != null && typeIsOurs) { setTestTypeId(''); autoFilled.current.typeId = null; }
            if (autoFilled.current.profileId != null && profileIsOurs) { setScoringProfileId(''); autoFilled.current.profileId = null; }
            return;
        }
        if (typeIsOurs) {
            setTestTypeId(type.id);
            autoFilled.current.typeId = type.id;
        }
        const profile = amcScoringProfile(scoringProfiles);
        if (profile && profileIsOurs) {
            setScoringProfileId(profile.id);
            autoFilled.current.profileId = profile.id;
        }
    };

    // File uploads — each slot tracks { url, name, key, uploading, progress }
    const initFileSlot = () => ({ url: '', name: '', key: '', uploading: false, progress: 0 });
    const [fileSlots, setFileSlots] = useState({
        test_pdf: initFileSlot(),
        explanation: initFileSlot(),
        watermarked: initFileSlot(),
        answer_key: initFileSlot(),
    });

    const updateFileSlot = (slot, updates) => setFileSlots(prev => ({ ...prev, [slot]: { ...prev[slot], ...updates } }));

    const handleFileUpload = async (slot, file) => {
        markDirty();
        updateFileSlot(slot, { uploading: true, progress: 0, name: file.name });
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('slot', slot);
            const res = await api.uploadWithProgress('/tests/upload-file', formData, (pct) => {
                updateFileSlot(slot, { progress: pct });
            });
            updateFileSlot(slot, { url: res.data.url, key: res.data.key, uploading: false, progress: 100 });
            // Auto-prefill title from test PDF filename
            if (slot === 'test_pdf' && !title) applyTitle(file.name.replace(/\.[^/.]+$/, ''));
        } catch (err) {
            console.error(`Upload failed for ${slot}:`, err);
            updateFileSlot(slot, { uploading: false, progress: 0, name: '' });
        }
    };

    const handleFileClear = async (slot) => {
        const s = fileSlots[slot];
        if (s.key) {
            try { await api.delete('/tests/delete-file', { key: s.key }); } catch {}
        }
        updateFileSlot(slot, initFileSlot());
    };

    const anyUploading = Object.values(fileSlots).some(s => s.uploading);

    // Unsaved changes detection
    const guardRef = React.useRef();
    const confirmRef = React.useRef();

    // FRQ submission method: 'text' or 'upload'
    const [frqSubmissionMethod, setFrqSubmissionMethod] = useState('text');
    const [releaseGrades, setReleaseGrades] = useState(true);
    const [useQuestionUi, setUseQuestionUi] = useState(false);
    // Retakeable / subset (vocab-style) config. Defaults preserve standard behavior.
    const [allowRetake, setAllowRetake] = useState(false);
    const [questionsPerAttempt, setQuestionsPerAttempt] = useState(''); // '' = whole test
    const [normalizeShortAnswers, setNormalizeShortAnswers] = useState(false);
    const [sectionTabMap, setSectionTabMap] = useState({}); // { [sIdx]: 'answers' | 'questions' }
    // With Question UI on, sections open on the Questions tab by default
    const sectionTab = (sIdx) => sectionTabMap[sIdx] ?? (useQuestionUi ? 'questions' : 'answers');
    const [sectionPageMap, setSectionPageMap] = useState({}); // { [sIdx]: pageIndex } — paginate the Questions tab so it renders a 20-question window, not all 250 (was freezing)

    // Parse flat-table JSON and populate form state
    const handleImportJson = (jsonData) => {
        console.log('[ImportJSON] received data, keys:', Object.keys(jsonData));
        const testMeta = jsonData.v2_tests || {};
        const jsonSections = jsonData.v2_test_sections || [];
        const jsonGroups = jsonData.v2_question_groups || [];
        const jsonQuestions = jsonData.v2_test_questions || [];
        const jsonOptions = jsonData.v2_question_options || [];
        const jsonParts = jsonData.v2_question_parts || [];

        // Index by id
        const groupsBySectionId = {};
        jsonGroups.forEach((g) => {
            if (!groupsBySectionId[g.section_id]) groupsBySectionId[g.section_id] = [];
            groupsBySectionId[g.section_id].push(g);
        });
        const questionsBySectionId = {};
        jsonQuestions.forEach((q) => {
            if (!questionsBySectionId[q.section_id]) questionsBySectionId[q.section_id] = [];
            questionsBySectionId[q.section_id].push(q);
        });
        const optionsByQuestionId = {};
        jsonOptions.forEach((o) => {
            if (!optionsByQuestionId[o.question_id]) optionsByQuestionId[o.question_id] = [];
            optionsByQuestionId[o.question_id].push(o);
        });
        const partsByQuestionId = {};
        jsonParts.forEach((p) => {
            if (!partsByQuestionId[p.question_id]) partsByQuestionId[p.question_id] = [];
            partsByQuestionId[p.question_id].push(p);
        });

        // Build part tree
        const buildPartTree = (flatParts) => {
            const map = {};
            const roots = [];
            flatParts.sort((a, b) => (a.part_order || 0) - (b.part_order || 0));
            for (const p of flatParts) map[p.id] = { ...p, subparts: [] };
            for (const p of flatParts) {
                if (p.parent_id && map[p.parent_id]) map[p.parent_id].subparts.push(map[p.id]);
                else roots.push(map[p.id]);
            }
            return roots;
        };
        const convertParts = (nodes) => nodes.map((n) => ({
            part_label: n.part_label,
            part_order: n.part_order,
            criteria_name: n.criteria_name || '',
            max_points: n.max_points,
            rubric_text: n.rubric_text || '',
            subparts: n.subparts?.length > 0 ? convertParts(n.subparts) : [],
        }));

        // Normalize question_type from JSON to our format
        const normalizeType = (t) => {
            if (t === 'free_response') return 'saq';
            if (t === 'short_answer' || t === 'sa') return 'multi_answer';
            if (t === 'multiple_choice') return 'mcq';
            return t || 'mcq';
        };

        // Build sections
        const sortedSections = [...jsonSections].sort((a, b) => a.section_number - b.section_number);
        const mapped = sortedSections.map((s) => {
            const sQuestions = (questionsBySectionId[s.id] || []).sort((a, b) => a.question_number - b.question_number);

            // Map groups: assign temp IDs so questions can reference them via group_ref
            const sGroups = (groupsBySectionId[s.id] || []).sort((a, b) => (a.order || 0) - (b.order || 0));
            const groupIdToTempId = {};
            const question_groups = sGroups.map((g, idx) => {
                const tempId = `group_${idx}`;
                groupIdToTempId[g.id] = tempId;
                return {
                    _temp_id: tempId,
                    group_label: g.group_label || null,
                    stimulus_text: g.stimulus_text || null,
                    stimulus_url: g.stimulus_url || null,
                    order: g.order || idx,
                };
            });

            return {
                section_name: s.section_name || '',
                section_number: s.section_number,
                section_type: s.section_type || null,
                time_limit_minutes: s.time_limit_minutes || '',
                instructions: s.instructions || '',
                _offset: 0,
                _actStyle: false,
                question_groups,
                questions: sQuestions.map((q) => {
                    const qOpts = (optionsByQuestionId[q.id] || []).sort((a, b) => (a.order || 0) - (b.order || 0));
                    const qParts = partsByQuestionId[q.id] || [];
                    const partTree = buildPartTree(qParts);
                    return {
                        question_number: q.question_number,
                        question_type: normalizeType(q.question_type),
                        question_prompt: q.question_prompt || '',
                        answer: q.answer || '',
                        max_points: q.max_points || 1,
                        is_multi_select: q.is_multi_select || false,
                        choice_group: q.choice_group || null,
                        accepted_answers: q.accepted_answers ? JSON.stringify(q.accepted_answers) : '',
                        metadata: q.metadata || null,
                        group_ref: q.group_id ? (groupIdToTempId[q.group_id] || null) : null,
                        options: qOpts.map((o) => ({
                            option_label: o.option_label,
                            option_text: o.option_text || '',
                            order: o.order,
                        })),
                        parts: convertParts(partTree),
                    };
                }),
            };
        });

        // Populate form
        console.log('[ImportJSON] mapped sections:', mapped.length, 'title:', testMeta.title);
        console.log('[ImportJSON] first section:', mapped[0]?.section_name, 'questions:', mapped[0]?.questions?.length);
        if (testMeta.title) setTitle(testMeta.title);
        setSections(mapped);
        setUseQuestionUi(true);
        markDirty();
    };

    // Preview
    const [previewOpen, setPreviewOpen] = useState(false);

    // AI auto-fill
    const [aiModalOpen, setAiModalOpen] = useState(false);

    // Dirty detection — tracks actual user interaction, not programmatic state changes
    const [isDirty, setIsDirty] = useState(false);
    const userInteracted = React.useRef(false);
    const markDirty = React.useCallback(() => { if (userInteracted.current) setIsDirty(true); }, []);

    // "Test saved ✓" flash next to the Save button (saving stays on the page)
    const [savedFlash, setSavedFlash] = useState(false);
    const savedFlashTimer = React.useRef(null);
    const flashSaved = React.useCallback(() => {
        setSavedFlash(true);
        clearTimeout(savedFlashTimer.current);
        savedFlashTimer.current = setTimeout(() => setSavedFlash(false), 2200);
    }, []);
    // A fresh create redirects here with ?saved=1 — show the flash once on arrival
    useEffect(() => {
        if (searchParams.get('saved')) {
            flashSaved();
            setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('saved'); return p; }, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Back to Library returns to the folder the test lives in, not the library root
    const goBackToLibrary = async () => {
        let target = '/admin/tests-v2';
        if (folderId) {
            try {
                const res = await api.get(`/test-categories/${folderId}/ancestors`);
                const ids = (res.data.data || []).map(c => c.id);
                if (ids.length) target = `/admin/tests-v2?path=${ids.join(',')}`;
            } catch { /* fall back to root */ }
        }
        guardRef.current?.navigate(() => navigate(target));
    };

    // ── MCQ option label helpers ───────────────────────────────────────────
    // Update a single option's label by sIdx/qIdx/oIdx
    const updateOptionLabel = (sIdx, qIdx, oIdx, value) => {
        markDirty();
        setSections(prev => prev.map((s, si) =>
            si !== sIdx ? s : {
                ...s,
                questions: s.questions.map((qq, qi) =>
                    qi !== qIdx ? qq : {
                        ...qq,
                        options: (qq.options || []).map((o, oi) =>
                            oi !== oIdx ? o : { ...o, option_label: value }
                        ),
                    }
                ),
            }
        ));
    };

    // Fetch dropdown data (+ test data if editing)
    const hasFetched = React.useRef(false);
    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        const fetchData = async () => {
            console.time('[EditTest] total load');
            try {
                console.time('[EditTest] API fetch');
                const promises = [
                    api.get('/test-categories', { tree: true }),
                    api.get('/scoring-profiles'),
                    api.get('/rubric-templates'),
                    api.get('/subjects'),
                    api.get('/test-types'),
                ];
                const duplicateId = searchParams.get('duplicate');
                if (isEdit) promises.push(api.get(`/tests/${id}`));
                else if (duplicateId) promises.push(api.get(`/tests/${duplicateId}`));

                const results = await Promise.all(promises);
                console.timeEnd('[EditTest] API fetch');

                // Non-blocking: chapter course options for the Chapter Course autocomplete
                api.get('/chapters/courses').then(r => setChapterCourses(r.data.data || [])).catch(() => {});

                console.time('[EditTest] set dropdowns');
                const [catRes, profRes, rubRes, subRes, typeRes] = results;

                const flat = [{ id: null, name: 'Test Library', label: 'Test Library', depth: 0 }, ...flattenFolders(catRes.data.data || [])];
                setFolders(flat);
                setScoringProfiles(profRes.data.data || []);
                setRubricTemplates(rubRes.data.data || []);
                setSubjects(subRes.data.data || []);
                setTestTypes(typeRes.data.data || []);
                console.timeEnd('[EditTest] set dropdowns');

                const sourceTest = results[5]?.data?.data;
                if ((isEdit || duplicateId) && sourceTest) {
                    console.log('[EditTest] test data:', sourceTest.v2_test_sections?.length, 'sections,', sourceTest.v2_test_sections?.reduce((n, s) => n + (s.v2_test_questions?.length || 0), 0), 'questions');
                    console.time('[EditTest] mapSectionFromAPI');
                    const mappedSections = sourceTest.v2_test_sections?.length > 0 ? sourceTest.v2_test_sections.map(mapSectionFromAPI) : null;
                    console.timeEnd('[EditTest] mapSectionFromAPI');

                    console.time('[EditTest] setState');
                    setTitle(duplicateId ? `${sourceTest.title || ''} (Copy)` : (sourceTest.title || ''));
                    setFolderId(sourceTest.category_id ?? null);
                    setScoringProfileId(sourceTest.scoring_profile_id || '');
                    setInitialScoringProfileId(sourceTest.scoring_profile_id || '');
                    setSubjectId(sourceTest.subject_id || '');
                    // Both edit and duplicate: reference original file URLs
                    // For duplicate, these are just references — no S3 copy until user replaces
                    setFileSlots({
                        test_pdf: { url: sourceTest.file_url || '', name: '', key: '', uploading: false, progress: 0 },
                        explanation: { url: sourceTest.explanations_file_url || '', name: '', key: '', uploading: false, progress: 0 },
                        watermarked: { url: sourceTest.watermarked_file_url || '', name: '', key: '', uploading: false, progress: 0 },
                        answer_key: { url: sourceTest.answer_key_url || '', name: '', key: '', uploading: false, progress: 0 },
                    });
                    setTestTypeId(sourceTest.test_type_id || '');
                    setTestCategory(sourceTest.test_category || 'regular');
                    setFrqSubmissionMethod(sourceTest.metadata?.frq_submission_method || 'text');
                    setReleaseGrades(sourceTest.release_grades !== false);
                    setUseQuestionUi(sourceTest.use_question_ui || false);
                    setAllowRetake(sourceTest.allow_retake || false);
                    setQuestionsPerAttempt(sourceTest.questions_per_attempt ?? '');
                    setNormalizeShortAnswers(sourceTest.normalize_short_answers || false);
                    if (mappedSections) {
                        setSections(mappedSections);
                        setInitialSectionsJson(JSON.stringify(buildParsedSections(mappedSections), STRIP_TEMP_KEYS));
                    }
                    console.timeEnd('[EditTest] setState');
                } else if (!isEdit) {
                    const prefillFolder = searchParams.get('folder');
                    if (prefillFolder) {
                        const match = flat.find(f => f.id === parseInt(prefillFolder));
                        if (match) setFolderId(parseInt(prefillFolder));
                    } else {
                        setFolderId(null);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch form data:', err);
            } finally {
                if (isEdit || isDuplicate) {
                    console.timeEnd('[EditTest] total load');
                    console.time('[EditTest] React render');
                    setLoading(false);
                }
            }
        };
        fetchData();
    }, [id, isEdit]);

    // Activate dirty tracking after first real user interaction (not programmatic mounts)
    useEffect(() => {
        if (loading) return;
        const activate = () => { userInteracted.current = true; };
        // Delay listener registration so deferred content can mount without triggering dirty
        const timer = setTimeout(() => {
            document.addEventListener('pointerdown', activate, { once: true });
            document.addEventListener('keydown', activate, { once: true });
        }, 500);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('pointerdown', activate);
            document.removeEventListener('keydown', activate);
        };
    }, [loading]);

    // ─── Section/Question Helpers ──────────────────────────────

    function createEmptySection(num) {
        return {
            section_name: `Section ${num}`,
            section_number: num,
            section_type: 'mixed',
            time_limit_minutes: '',
            questions: [],
        };
    }

    function createEmptyQuestion(num, type = 'mcq') {
        return {
            question_number: num,
            question_type: type,
            answer: '',
            max_points: type === 'mcq' ? 1 : '',
            accepted_answers: '',
            choice_group: '',
            chapter_number: '',
            options: type === 'mcq' ? DEFAULT_OPTIONS.map((l, i) => ({ option_label: l, option_text: '', order: i })) : [],
            parts: [],
        };
    }

    function createEmptyPart(num) {
        return {
            part_label: String.fromCharCode(96 + num), // a, b, c...
            part_order: num,
            criteria_name: '',
            max_points: 1,
            rubric_text: '',
            rubric_template_id: '',
        };
    }

    function mapPartsFromAPI(parts) {
        return (parts || []).map(p => ({
            id: p.id,
            part_label: p.part_label,
            part_order: p.part_order,
            criteria_name: p.criteria_name || '',
            max_points: p.max_points,
            rubric_text: p.rubric_text || '',
            rubric_template_id: p.rubric_template_id || '',
            subparts: mapPartsFromAPI(p.subparts || p.v2_question_parts || []),
        }));
    }

    function mapSectionFromAPI(section) {
        // Map groups from API with temp IDs for group_ref linkage
        const apiGroups = section.v2_question_groups || [];
        const groupIdToTempId = {};
        const question_groups = apiGroups.map((g, idx) => {
            const tempId = `group_${idx}`;
            groupIdToTempId[g.id] = tempId;
            return {
                _temp_id: tempId,
                group_label: g.group_label || null,
                stimulus_text: g.stimulus_text || null,
                stimulus_url: g.stimulus_url || null,
                order: g.order || idx,
            };
        });

        return {
            id: section.id,
            section_name: section.section_name || '',
            section_number: section.section_number,
            section_type: section.section_type || 'mixed',
            time_limit_minutes: section.time_limit_minutes || '',
            question_groups: question_groups.map((g, gi) => ({ ...g, id: apiGroups[gi]?.id })),
            questions: (section.v2_test_questions || []).map(q => ({
                id: q.id,
                question_number: q.question_number,
                question_type: q.question_type === 'sa' ? 'multi_answer' : q.question_type,
                question_prompt: q.question_prompt || '',
                answer: q.answer || '',
                max_points: q.max_points || 1,
                accepted_answers: q.accepted_answers ? JSON.stringify(q.accepted_answers) : '',
                choice_group: q.choice_group || '',
                chapter_number: q.v2_chapter ? q.v2_chapter.number : '',
                chapter_id: q.chapter_id || null,
                _loaded_chapter_number: q.v2_chapter ? q.v2_chapter.number : '',
                _chapter_course: q.v2_chapter ? q.v2_chapter.course : '',
                _chapter_kind: q.v2_chapter ? q.v2_chapter.kind : '',
                metadata: q.metadata || null,
                group_ref: q.group_id ? (groupIdToTempId[q.group_id] || null) : null,
                options: (q.v2_question_options || []).map(o => ({
                    id: o.id,
                    option_label: o.option_label, option_text: o.option_text || '', order: o.order,
                })),
                parts: mapPartsFromAPI(q.v2_question_parts || []),
            })),
        };
    }

    function flattenFolders(items, depth = 0) {
        const result = [];
        for (const item of items) {
            const indent = depth > 0 ? '  '.repeat(depth - 1) + '└ ' : '';
            result.push({ id: item.id, name: item.name, label: indent + item.name, depth });
            if (item.children?.length > 0) {
                result.push(...flattenFolders(item.children, depth + 1));
            }
        }
        return result;
    }

    // ─── State Updaters ────────────────────────────────────────

    const updateSection = (sIdx, field, value) => {
        markDirty();
        setSections(prev => prev.map((s, i) => i === sIdx ? { ...s, [field]: value } : s));
    };

    const addSection = () => {
        markDirty();
        setSections(prev => [...prev, createEmptySection(prev.length + 1)]);
    };

    const removeSection = (sIdx) => {
        markDirty();
        setSections(prev => prev.filter((_, i) => i !== sIdx).map((s, i) => ({ ...s, section_number: i + 1 })));
    };

    // Set a question's chapter number. Course context resolution, in order:
    // the question's own course (already tagged) -> another tagged question in
    // the test -> the session's remembered choice -> ask once via dialog.
    const setQuestionChapter = (sIdx, qIdx, value) => {
        const q = sections[sIdx]?.questions[qIdx];
        if (!q) return;
        if (value === '' || q._chapter_course) {
            updateQuestion(sIdx, qIdx, 'chapter_number', value);
            return;
        }
        let inferred = sessionChapterCourse;
        if (!inferred) {
            outer: for (const s of sections) {
                for (const sq of s.questions || []) {
                    if (sq._chapter_course) { inferred = { course: sq._chapter_course, kind: sq._chapter_kind }; break outer; }
                }
            }
        }
        if (inferred) {
            markDirty();
            setSections(prev => prev.map((s, si) => si !== sIdx ? s : {
                ...s,
                questions: s.questions.map((qq, qi) => qi !== qIdx ? qq : {
                    ...qq, chapter_number: value, _chapter_course: inferred.course, _chapter_kind: inferred.kind,
                }),
            }));
            return;
        }
        setDialogCourse('');
        setDialogKind('chapter');
        setCourseDialog({ sIdx, qIdx, value });
    };

    const confirmCourseDialog = () => {
        const { sIdx, qIdx, value } = courseDialog;
        const chosen = { course: dialogCourse.trim(), kind: dialogKind };
        setCourseDialog(null);
        if (!chosen.course) return;
        setSessionChapterCourse(chosen);
        markDirty();
        setSections(prev => prev.map((s, si) => si !== sIdx ? s : {
            ...s,
            questions: s.questions.map((qq, qi) => qi !== qIdx ? qq : {
                ...qq, chapter_number: value, _chapter_course: chosen.course, _chapter_kind: chosen.kind,
            }),
        }));
    };

    // Edit a section's passage (question group) by its temp ref
    const updateGroup = (sIdx, tempId, patch) => {
        markDirty();
        setSections(prev => prev.map((s, si) => si !== sIdx ? s : {
            ...s,
            question_groups: (s.question_groups || []).map(g =>
                g._temp_id === tempId ? { ...g, ...patch } : g
            ),
        }));
    };

    const updateQuestion = (sIdx, qIdx, field, value) => {
        markDirty();
        setSections(prev => prev.map((s, si) =>
            si === sIdx ? {
                ...s,
                questions: s.questions.map((q, qi) => qi === qIdx ? { ...q, [field]: value } : q),
            } : s
        ));
    };

    // Recreate ONE stimulus group per distinct source group among the payloads
    // (multiple picked questions sharing a passage share the recreated group).
    const recreateGroups = (payloads, existingGroups, keyPrefix) => {
        const groups = [...existingGroups];
        const refBySourceId = {};
        for (const iq of payloads) {
            const g = iq._source_group;
            if (g && !(g.id in refBySourceId)) {
                const tempId = `impgrp_${keyPrefix}_${g.id}_${groups.length}`;
                groups.push({
                    _temp_id: tempId,
                    group_label: g.group_label,
                    stimulus_text: g.stimulus_text,
                    stimulus_url: g.stimulus_url,
                    order: groups.length,
                });
                refBySourceId[g.id] = tempId;
            }
        }
        return { groups, refBySourceId };
    };

    // Build a brand-new section from imported/generated payloads.
    const buildNewSection = (payloads, sectionNumber) => {
        const { groups, refBySourceId } = recreateGroups(payloads, [], sectionNumber);
        return {
            section_name: `Section ${sectionNumber}`,
            section_number: sectionNumber,
            section_type: 'mixed',
            time_limit_minutes: '',
            question_groups: groups,
            questions: payloads.map(({ _source_group, ...fields }, qi) => ({
                ...fields,
                question_number: qi + 1,
                group_ref: _source_group ? refBySourceId[_source_group.id] : null,
            })),
        };
    };

    // Append payloads to an existing section: renumber after its last question,
    // recreate stimulus groups (one per source group).
    const appendPayloadsToSection = (s, payloads, keyPrefix) => {
        const offset = s._offset || 0;
        let nextNum = offset + s.questions.length + 1;
        const { groups, refBySourceId } = recreateGroups(payloads, s.question_groups || [], keyPrefix);
        const newQs = payloads.map(({ _source_group, ...fields }) => ({
            ...fields,
            question_number: nextNum++,
            group_ref: _source_group ? refBySourceId[_source_group.id] : null,
        }));
        return { ...s, question_groups: groups, questions: [...s.questions, ...newQs] };
    };

    // Append picks from the Import Questions modal. importTarget: 'new' -> a
    // fresh section at the end; number -> appended to that existing section.
    const handleImportQuestions = (importedRaw) => {
        const target = importTarget;
        setImportTarget(null);
        if (target == null || !importedRaw?.length) return;
        // clicking order may scatter passage-mates — keep them adjacent
        const imported = clusterByGroup(importedRaw);
        markDirty();
        const targetNumber = target === 'new' ? sections.length + 1 : target + 1;
        setGenDupWarnings(findIncomingDups([{ sectionNumber: targetNumber, payloads: imported }], 'Imported into'));
        if (target === 'new') {
            setSections(prev => [...prev, buildNewSection(imported, prev.length + 1)]);
            return;
        }
        setSections(prev => prev.map((s, i) => i !== target ? s : appendPayloadsToSection(s, imported, `s${i}`)));
    };

    // Duplicates the pickers can't fully see: their pools are per-chapter/test,
    // but the same question text can already sit in THIS test (imported
    // earlier, or pulled from an overlapping source test / other chapter).
    // Surface those for a manual swap/remove instead of silently keeping twins.
    // Fires for BOTH Generate and Import inserts.
    const [genDupWarnings, setGenDupWarnings] = useState([]);
    const findIncomingDups = (batches, verb) => {
        const existingByKey = new Map();
        sections.forEach((s, si) => (s.questions || []).forEach((q) => {
            const k = contentKey(q);
            if (k && !existingByKey.has(k)) existingByKey.set(k, `Section ${si + 1} Q${q.question_number}`);
        }));
        const warnings = [];
        for (const gs of batches) {
            for (const p of gs.payloads) {
                const where = contentKey(p) && existingByKey.get(contentKey(p));
                if (where) warnings.push(`${verb} Section ${gs.sectionNumber}, already in the test at ${where}: “${stripPreview(p.question_prompt, 80)}”`);
            }
        }
        return warnings;
    };

    // Persistent duplicate marker for the section question lists: unlike the
    // one-shot Alert above, this recomputes live so content twins stay visible
    // (and disappear as soon as one copy is edited or removed).
    const dupLocations = React.useMemo(() => {
        const byKey = new Map();
        sections.forEach((s, si) => (s.questions || []).forEach((q, qi) => {
            const k = contentKey(q);
            if (!k) return;
            if (!byKey.has(k)) byKey.set(k, []);
            byKey.get(k).push({ si, qi, qnum: q.question_number, label: `S${si + 1} Q${q.question_number}` });
        }));
        return byKey;
    }, [sections]);
    // Chip names the WHOLE duplicate group ("Q1, Q3" — or "S1 Q1, S2 Q3" when
    // the twins span sections) so each copy shows where all the others are.
    const dupChipFor = (q, si, qi, sx = {}) => {
        const k = contentKey(q);
        const list = k ? dupLocations.get(k) : null;
        if (!list || list.length < 2) return null;
        const sameSection = list.every((e) => e.si === list[0].si);
        const groupLabel = list.map((e) => (sameSection ? `Q${e.qnum}` : e.label)).join(', ');
        return (
            <Chip label={groupLabel} size="small" color="error" variant="outlined"
                title={`Duplicate question text (${groupLabel}) — swap or remove a copy`}
                sx={{ height: 18, fontSize: 10, fontWeight: 700, flexShrink: 0, ...sx }} />
        );
    };

    // Split-passage warning (same pattern as the duplicate chips): questions
    // sharing a stimulus group that are NOT adjacent make the passage render
    // repeatedly and its "Questions N–M" label lie — surface it live so the
    // admin drags them together. Per section: group_ref -> member positions.
    const splitGroups = React.useMemo(() => sections.map((s) => {
        const byRef = new Map();
        (s.questions || []).forEach((q, qi) => {
            if (!q.group_ref) return;
            if (!byRef.has(q.group_ref)) byRef.set(q.group_ref, []);
            byRef.get(q.group_ref).push(qi);
        });
        const split = new Map();
        byRef.forEach((idxs, ref) => {
            if (idxs[idxs.length - 1] - idxs[0] !== idxs.length - 1) {
                split.set(ref, idxs.map((qi) => `Q${s.questions[qi].question_number}`));
            }
        });
        return split;
    }), [sections]);
    const splitChipFor = (q, si, sx = {}) => {
        const members = q.group_ref ? splitGroups[si]?.get(q.group_ref) : null;
        if (!members) return null;
        return (
            <Chip label={`passage split: ${members.join(', ')}`} size="small" color="warning" variant="outlined"
                title="These questions share one passage but aren't next to each other — drag them together so the passage reads as one block"
                sx={{ height: 18, fontSize: 10, fontWeight: 700, flexShrink: 0, ...sx }} />
        );
    };

    // Apply generator output: section numbers within the current set append to
    // that existing section; numbers past the end become new sections in order.
    const handleGenerate = (generatedSections) => {
        setGenerateOpen(false);
        if (!generatedSections?.length) return;
        setGenDupWarnings(findIncomingDups(generatedSections, 'Generated into'));
        markDirty();
        setSections(prev => {
            let out = [...prev];
            const newOnes = [];
            for (const gs of [...generatedSections].sort((a, b) => a.sectionNumber - b.sectionNumber)) {
                if (gs.sectionNumber >= 1 && gs.sectionNumber <= prev.length) {
                    const i = gs.sectionNumber - 1;
                    out = out.map((s, si) => si !== i ? s : appendPayloadsToSection(s, gs.payloads, `gen${i}`));
                } else {
                    newOnes.push(gs);
                }
            }
            for (const gs of newOnes) out = [...out, buildNewSection(gs.payloads, out.length + 1)];
            return out;
        });
    };

    const addQuestion = (sIdx) => {
        markDirty();
        setSections(prev => prev.map((s, si) =>
            si === sIdx ? {
                ...s,
                questions: [...s.questions, createEmptyQuestion(s.questions.length + 1, s.section_type)],
            } : s
        ));
    };

    const removeQuestion = (sIdx, qIdx) => {
        markDirty();
        setSections(prev => prev.map((s, si) =>
            si === sIdx ? {
                ...s,
                questions: s.questions.filter((_, qi) => qi !== qIdx).map((q, i) => ({ ...q, question_number: i + 1 })),
            } : s
        ));
    };

    const addPart = (sIdx, qIdx) => {
        markDirty();
        setSections(prev => prev.map((s, si) =>
            si === sIdx ? {
                ...s,
                questions: s.questions.map((q, qi) =>
                    qi === qIdx ? { ...q, parts: [...q.parts, createEmptyPart(q.parts.length + 1)] } : q
                ),
            } : s
        ));
    };

    const removePart = (sIdx, qIdx, pIdx) => {
        markDirty();
        setSections(prev => prev.map((s, si) =>
            si === sIdx ? {
                ...s,
                questions: s.questions.map((q, qi) =>
                    qi === qIdx ? {
                        ...q,
                        parts: q.parts.filter((_, pi) => pi !== pIdx).map((p, i) => ({
                            ...p, part_order: i + 1,
                            part_label: `Part ${String.fromCharCode(65 + i)}`,
                        })),
                    } : q
                ),
            } : s
        ));
    };

    const updatePart = (sIdx, qIdx, pIdx, field, value) => {
        markDirty();
        setSections(prev => prev.map((s, si) =>
            si === sIdx ? {
                ...s,
                questions: s.questions.map((q, qi) =>
                    qi === qIdx ? {
                        ...q,
                        parts: q.parts.map((p, pi) => pi === pIdx ? { ...p, [field]: value } : p),
                    } : q
                ),
            } : s
        ));
    };

    // ─── Add Subject ────────────────────────────────────────────

    const [addSubjectSource, setAddSubjectSource] = useState('test'); // 'test' or 'profile'

    const handleAddSubject = async () => {
        if (!newSubjectName.trim()) return;
        try {
            const res = await api.post('/subjects', { name: newSubjectName.trim(), label: newSubjectName.trim() });
            const created = res.data.data;
            setSubjects(prev => [...prev, created]);
            if (addSubjectSource === 'profile') {
                setNewProfileSubject(created.name);
            } else {
                setSubjectId(created.id);
            }
            setNewSubjectName('');
            setAddSubjectOpen(false);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to create subject');
        }
    };

    // handleAddProfile is now handled by handleSaveProfile (supports both create and edit)

    const resetProfileForm = () => {
        setEditingProfileId(null);
        setNewProfileName('');
        setNewProfileSubject('');
        setNewProfileMcqMultiplier('');
        setNewProfileFrqDefaultMultiplier('');
        setNewProfileFrqMults([]);
        setNewProfileFrqTargets('');
        setNewProfileConversion([
            { score: '5', min: '', max: '' },
            { score: '4', min: '', max: '' },
            { score: '3', min: '', max: '' },
            { score: '2', min: '', max: '' },
            { score: '1', min: '', max: '' },
        ]);
    };

    const openEditProfile = (profileId) => {
        const profile = scoringProfiles.find(p => p.id === profileId);
        if (!profile) return;
        setEditingProfileId(profileId);
        setNewProfileName(profile.name || '');
        setNewProfileSubject(profile.subject || '');
        setNewProfileMcqMultiplier(profile.mcq_multiplier != null ? String(profile.mcq_multiplier) : '');
        setNewProfileFrqDefaultMultiplier(profile.metadata?.frq_default_multiplier != null ? String(profile.metadata.frq_default_multiplier) : '');
        setNewProfileFrqMults((profile.metadata?.frq_multipliers || []).map(m => ({
            ...m,
            _mode: m.question ? 'question' : 'type',
        })));
        setNewProfileFrqTargets((profile.frq_targets || []).join(', '));
        const sc = profile.score_conversion || {};
        setNewProfileConversion(
            ['5', '4', '3', '2', '1'].map(s => {
                const range = sc[s];
                return { score: s, min: range ? String(range[0]) : '', max: range ? String(range[1]) : '' };
            })
        );
        setAddProfileOpen(true);
    };

    const handleSaveProfile = async () => {
        if (!newProfileName.trim()) return;
        try {
            const score_conversion = {};
            newProfileConversion.forEach(c => {
                if (c.min !== '' && c.max !== '') {
                    score_conversion[c.score] = [parseInt(c.min), parseInt(c.max)];
                }
            });

            const parsedTargets = newProfileFrqTargets.trim()
                ? newProfileFrqTargets.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n))
                : null;

            const payload = {
                name: newProfileName.trim(),
                subject: newProfileSubject.trim() || null,
                type_weights: {},
                score_conversion: Object.keys(score_conversion).length > 0 ? score_conversion : null,
                mcq_multiplier: newProfileMcqMultiplier ? parseFloat(newProfileMcqMultiplier) : null,
                frq_targets: parsedTargets && parsedTargets.length > 0 ? parsedTargets : null,
                metadata: {
                    // Keep whatever this form doesn't edit — the ACT profile's section curves and
                    // the AMC profile's answer points + scale live in metadata too, and PUT replaces
                    // the whole object. Without this, saving a renamed ACT/AMC profile wiped them.
                    ...(editingProfileId ? (scoringProfiles.find(p => p.id === editingProfileId)?.metadata || {}) : {}),
                    frq_multipliers: newProfileFrqMults,
                    frq_default_multiplier: newProfileFrqDefaultMultiplier ? parseFloat(newProfileFrqDefaultMultiplier) : null,
                },
            };

            if (editingProfileId) {
                // Update existing
                const res = await api.put(`/scoring-profiles/${editingProfileId}`, payload);
                setScoringProfiles(prev => prev.map(p => p.id === editingProfileId ? res.data.data : p));
                // Prompt to recompute composites
                setRecomputeProfileId(editingProfileId);
                setRecomputeDialogOpen(true);
            } else {
                // Create new
                const res = await api.post('/scoring-profiles', payload);
                const created = res.data.data;
                setScoringProfiles(prev => [...prev, created]);
                setScoringProfileId(created.id);
            }

            resetProfileForm();
            setAddProfileOpen(false);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to save scoring profile');
        }
    };

    const handleRecomputeComposites = async () => {
        if (!recomputeProfileId) return;
        setRecomputing(true);
        try {
            const res = await api.post(`/scoring-profiles/${recomputeProfileId}/recompute-composites`);
            setRecomputeDialogOpen(false);
            const shouldNavigate = recomputeNavigateAfter;
            setRecomputeProfileId(null);
            setRecomputeNavigateAfter(false);
            alert(`Successfully recomputed ${res.data.updated} session(s).`);
            if (shouldNavigate) navigate('/admin/tests-v2', { replace: true });
        } catch (err) {
            alert('Failed to recompute: ' + (err.response?.data?.message || err.message));
        } finally {
            setRecomputing(false);
        }
    };

    const handleRecomputeSkip = () => {
        const shouldNavigate = recomputeNavigateAfter;
        setRecomputeDialogOpen(false);
        setRecomputeProfileId(null);
        setRecomputeNavigateAfter(false);
        if (shouldNavigate) navigate('/admin/tests-v2', { replace: true });
    };

    const handleDeleteManageable = (type, item) => {
        confirmRef.current?.open({
            title: `Delete ${type}`,
            message: `Are you sure you want to delete "${item.label || item.name}"?`,
            confirmLabel: 'Delete',
            onConfirm: async () => {
                const endpointMap = { 'Subject': 'subjects', 'Scoring Profile': 'scoring-profiles', 'Test Type': 'test-types' };
                const setterMap = { 'Subject': setSubjects, 'Scoring Profile': setScoringProfiles, 'Test Type': setTestTypes };
                const idSetterMap = { 'Subject': setSubjectId, 'Scoring Profile': setScoringProfileId, 'Test Type': setTestTypeId };
                const currentIdMap = { 'Subject': subjectId, 'Scoring Profile': scoringProfileId, 'Test Type': testTypeId };
                const endpoint = endpointMap[type];
                const setter = setterMap[type];
                const idSetter = idSetterMap[type];
                const currentId = currentIdMap[type];

                try {
                    await api.delete(`/${endpoint}/${item.id}`);
                    setter(prev => prev.filter(x => x.id !== item.id));
                    if (currentId === item.id) idSetter('');
                } catch (err) {
                    const data = err.response?.data;
                    if (data?.needsConfirm) {
                        confirmRef.current?.open({
                            title: `Remove ${type}`,
                            message: data.message,
                            confirmLabel: 'Remove Anyway',
                            onConfirm: async () => {
                                try {
                                    await api.delete(`/${endpoint}/${item.id}`, { confirm: 'true' });
                                    setter(prev => prev.filter(x => x.id !== item.id));
                                    if (currentId === item.id) idSetter('');
                                } catch (err2) {
                                    alert(err2.response?.data?.message || 'Failed to delete');
                                }
                            },
                        });
                    } else {
                        alert(data?.message || 'Failed to delete');
                    }
                }
            },
        });
    };

    // ─── AI Auto-fill ────────────────────────────────────────────

    const handleAIApply = (data) => {
        // data comes from either auto or manual mode, already in full format
        const mapped = (data.sections || []).map((s, idx) => ({
            section_name: s.section_name || s.name || '',
            section_number: idx + 1,
            section_type: s.section_type || 'mcq',
            time_limit_minutes: '',
            questions: (s.questions || []).map(q => ({
                question_number: q.question_number,
                question_type: q.question_type || 'mcq',
                answer: q.answer || '',
                max_points: q.max_points || 1,
                accepted_answers: q.accepted_answers ? JSON.stringify(q.accepted_answers) : '',
                options: q.options || [],
                parts: q.parts || [],
            })),
        }));

        setSections(mapped);

        if (!title && fileSlots.test_pdf.name) {
            applyTitle(fileSlots.test_pdf.name.replace(/\.[^/.]+$/, ''));
        }
    };

    // ─── Save ──────────────────────────────────────────────────

    const handleSave = async () => {
        if (!title.trim()) {
            alert('Title is required');
            return;
        }

        if (anyUploading) {
            alert('Please wait for file uploads to finish');
            return;
        }

        // Check for duplicate title (skip on edit — same test can keep its name)
        if (!isEdit) {
            try {
                const res = await api.get('/tests', { search: title.trim(), limit: 100 });
                const exact = (res.data.data || []).find(t => t.title.toLowerCase() === title.trim().toLowerCase());
                if (exact && !window.confirm(`A test named "${exact.title}" already exists. Create anyway?`)) {
                    return;
                }
            } catch { /* proceed if check fails */ }
        }

        setSaving(true);
        try {
            // Build sections with parsed accepted_answers
            const parsedSections = buildParsedSections(sections);

            // Retake/subset/normalize apply only to the Vocab test type — never
            // persist them for other types (so switching type away clears them).
            const isVocab = testTypes.find((t) => t.id === testTypeId)?.name === 'Vocab';

            const testPayload = {
                title,
                category_id: folderId || null,
                scoring_profile_id: scoringProfileId || null,
                subject_id: subjectId || null,
                test_type_id: testTypeId || null,
                test_category: testCategory,
                release_grades: releaseGrades,
                use_question_ui: useQuestionUi,
                allow_retake: isVocab ? allowRetake : false,
                questions_per_attempt: isVocab && allowRetake && questionsPerAttempt !== '' ? parseInt(questionsPerAttempt, 10) : null,
                normalize_short_answers: isVocab ? normalizeShortAnswers : false,
                file_url: fileSlots.test_pdf.url || null,
                explanations_file_url: fileSlots.explanation.url || null,
                watermarked_file_url: fileSlots.watermarked.url || null,
                answer_key_url: fileSlots.answer_key.url || null,
                metadata: { frq_submission_method: frqSubmissionMethod },
            };

            if (isEdit) {
                await api.put(`/tests/${id}`, testPayload);
                // Only update structure if sections/questions actually changed.
                // Both sides use buildParsedSections + STRIP_TEMP_KEYS so the
                // comparison isn't thrown off by type coercions or temp UI state.
                const structureChanged = JSON.stringify(parsedSections, STRIP_TEMP_KEYS) !== initialSectionsJson;
                if (structureChanged) {
                    try {
                        await api.put(`/tests/${id}/structure`, { sections: parsedSections });
                    } catch (structErr) {
                        if (structErr.response?.status === 409 && structErr.response?.data?.session_count) {
                            // Student sessions exist — ask admin to confirm
                            const count = structErr.response.data.session_count;
                            setSaving(false);
                            confirmRef.current?.open({
                                title: 'Student sessions exist',
                                message: `${count} student session(s) already exist for this test. Editing the structure may invalidate their scores. Continue anyway?`,
                                confirmText: 'Edit Anyway',
                                onConfirm: async () => {
                                    setSaving(true);
                                    try {
                                        await api.put(`/tests/${id}/structure?force=true`, { sections: parsedSections });
                                        setIsDirty(false);
                                        setInitialSectionsJson(JSON.stringify(parsedSections, STRIP_TEMP_KEYS));
                                        flashSaved();
                                    } catch (forceErr) {
                                        alert(forceErr.response?.data?.message || 'Failed to save test structure');
                                    } finally {
                                        setSaving(false);
                                    }
                                },
                            });
                            return;
                        }
                        throw structErr;
                    }
                }
            } else {
                // Fresh create: jump to the new test's edit page (stays "on the page"
                // from the user's perspective) and flash the saved indicator there.
                const created = await api.post('/tests', { ...testPayload, sections: parsedSections });
                setIsDirty(false);
                const newId = created.data?.data?.id;
                navigate(newId ? `/admin/tests-v2/${newId}/edit?saved=1` : '/admin/tests-v2', { replace: true });
                return;
            }

            // Saved — stay on the page, reset the change baseline, flash confirmation
            setIsDirty(false);
            setInitialSectionsJson(JSON.stringify(parsedSections, STRIP_TEMP_KEYS));
            flashSaved();

            // If scoring profile changed on edit, offer to recompute
            if (isEdit && scoringProfileId && scoringProfileId !== initialScoringProfileId) {
                setSaving(false);
                setRecomputeProfileId(scoringProfileId);
                setRecomputeNavigateAfter(false);
                setRecomputeDialogOpen(true);
                return;
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to save test');
        } finally {
            setSaving(false);
        }
    };

    // ─── Render ────────────────────────────────────────────────

    // Each section defers its heavy content independently

    if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

    return (
        <UnsavedChangesGuard ref={guardRef} hasChanges={isDirty}>
        <Box m="20px">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Header title={isEdit ? 'Edit Test' : 'Create Test'} subtitle="Build test structure with sections and questions" />
                <Box display="flex" gap={1} alignItems="center">
                    <Typography variant="body2" sx={{
                        color: '#51BE90', fontWeight: 700, whiteSpace: 'nowrap',
                        opacity: savedFlash ? 1 : 0, transition: 'opacity .6s ease',
                    }}>
                        Test saved ✓
                    </Typography>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving || anyUploading}
                    >
                        {saving ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save Test' : 'Add Test')}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setPreviewOpen(true)}
                    >
                        Preview
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={goBackToLibrary}
                    >
                        Back to Library
                    </Button>
                </Box>
            </Box>

            {/* Generator duplicate warning — same question text already in the test */}
            {genDupWarnings.length > 0 && (
                <Alert severity="warning" onClose={() => setGenDupWarnings([])} sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                        {genDupWarnings.length} added question{genDupWarnings.length > 1 ? 's' : ''} duplicate{genDupWarnings.length > 1 ? '' : 's'} content already in this test — swap in a different question or remove one copy.
                    </Typography>
                    <Box component="ul" sx={{ m: '4px 0 0', pl: 2.5 }}>
                        {genDupWarnings.map((w, i) => (
                            <Typography key={i} component="li" variant="caption">{w}</Typography>
                        ))}
                    </Box>
                </Alert>
            )}

            {/* File Uploads */}
            <Paper sx={{ p: 3, bgcolor: colors.primary[600], mb: 3 }}>
                <Typography variant="h6" fontWeight={600} mb={2}>Files</Typography>
                <Box display="flex" gap={2} sx={{ '& > *': { flex: '1 1 0', minWidth: 0 } }}>
                    <FileDropZone
                        label="Test PDF"
                        hint="The test itself — students + admins see this file. Teachers see the Watermarked PDF instead."
                        uploadedUrl={fileSlots.test_pdf.url}
                        uploadedName={fileSlots.test_pdf.name}
                        uploading={fileSlots.test_pdf.uploading}
                        progress={fileSlots.test_pdf.progress}
                        onFile={(file) => handleFileUpload('test_pdf', file)}
                        onClear={() => handleFileClear('test_pdf')}
                    />
                    <FileDropZone
                        label="Explanation File"
                        hint="Explanations for questions & answers (incl. FRQs). Teachers + admins, in Review Mode only."
                        uploadedUrl={fileSlots.explanation.url}
                        uploadedName={fileSlots.explanation.name}
                        uploading={fileSlots.explanation.uploading}
                        progress={fileSlots.explanation.progress}
                        onFile={(file) => handleFileUpload('explanation', file)}
                        onClear={() => handleFileClear('explanation')}
                    />
                    <FileDropZone
                        label="Watermarked PDF (Teacher)"
                        hint="What teachers see instead of the Test PDF. Teachers + admins only."
                        uploadedUrl={fileSlots.watermarked.url}
                        uploadedName={fileSlots.watermarked.name}
                        uploading={fileSlots.watermarked.uploading}
                        progress={fileSlots.watermarked.progress}
                        onFile={(file) => handleFileUpload('watermarked', file)}
                        onClear={() => handleFileClear('watermarked')}
                    />
                    <FileDropZone
                        label="Answer Key"
                        hint="Only used by AI grading — not viewable anywhere except here."
                        uploadedUrl={fileSlots.answer_key.url}
                        uploadedName={fileSlots.answer_key.name}
                        uploading={fileSlots.answer_key.uploading}
                        progress={fileSlots.answer_key.progress}
                        onFile={(file) => handleFileUpload('answer_key', file)}
                        onClear={() => handleFileClear('answer_key')}
                    />
                </Box>

                {/* Question UI Mode */}
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={useQuestionUi}
                                onChange={(e) => { markDirty(); setUseQuestionUi(e.target.checked); }}
                            />
                        }
                        label={<Typography variant="body2">Use Question UI (Beta) — render questions from data instead of PDF</Typography>}
                    />
                    {useQuestionUi && (
                        <Box
                            sx={{
                                mt: 1, p: 3, border: '2px dashed', borderColor: colors.grey[600],
                                borderRadius: 1, textAlign: 'center', cursor: 'pointer',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
                                '&:hover': { borderColor: colors.blueAccent[500], bgcolor: `${colors.blueAccent[500]}08` },
                            }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const file = e.dataTransfer.files?.[0];
                                if (!file || !file.name.endsWith('.json')) {
                                    alert('Please drop a .json file');
                                    return;
                                }
                                file.text().then((text) => {
                                    try {
                                        const data = JSON.parse(text);
                                        handleImportJson(data);
                                    } catch (err) {
                                        console.error('JSON parse error:', err);
                                        alert('Failed to parse JSON: ' + err.message);
                                    }
                                }).catch((err) => {
                                    console.error('File read error:', err);
                                    alert('Failed to read file: ' + err.message);
                                });
                            }}
                            onClick={() => {
                                console.log('[ImportJSON] click handler fired');
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = '.json';
                                input.onchange = (e) => {
                                    const file = e.target.files?.[0];
                                    console.log('[ImportJSON] file selected:', file?.name, file?.size);
                                    if (!file) return;
                                    file.text().then((text) => {
                                        try {
                                            const data = JSON.parse(text);
                                            handleImportJson(data);
                                        } catch (err) {
                                            console.error('JSON parse error:', err);
                                            alert('Failed to parse JSON: ' + err.message);
                                        }
                                    }).catch((err) => {
                                        console.error('File read error:', err);
                                        alert('Failed to read file: ' + err.message);
                                    });
                                };
                                input.click();
                            }}
                        >
                            <UploadFileIcon sx={{ fontSize: 32, color: colors.grey[500] }} />
                            <Typography variant="body2" color="text.secondary">
                                Drop or click to import a JSON test file
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Populates title, sections, questions, options, and parts for review
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Test Metadata */}
            <Paper sx={{ p: 3, bgcolor: colors.primary[600], mb: 3 }}>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
                    {/* Left Column */}
                    <Box display="flex" flexDirection="column" gap={2} sx={{ minWidth: 0 }}>
                    <Box display="flex" gap={2}>
                        <TextField
                            label="Test Title"
                            value={title}
                            onChange={(e) => { markDirty(); applyTitle(e.target.value); }}
                            required
                            sx={{ flex: 7 }}
                        />
                        <Box sx={{ flex: 3 }}>
                        <ManageableAutocomplete
                            label="Test Type"
                            options={testTypes}
                            value={testTypes.find(t => t.id === testTypeId) || null}
                            onChange={(val) => {
                                markDirty();
                                setTestTypeId(val?.id || '');
                                // Auto-enable ACT-style on all sections when test type is ACT
                                if (val?.name?.toUpperCase() === 'ACT') {
                                    const ACT_ODD = ['A', 'B', 'C', 'D', 'E'];
                                    const ACT_EVEN = ['F', 'G', 'H', 'J', 'K'];
                                    setSections(prev => prev.map(s => ({
                                        ...s,
                                        _actStyle: true,
                                        questions: s.questions.map(q => {
                                            if (q.question_type !== 'mcq') return q;
                                            const numChoices = q.options?.length || 4;
                                            const isOdd = q.question_number % 2 === 1;
                                            const labels = (isOdd ? ACT_ODD : ACT_EVEN).slice(0, numChoices);
                                            return { ...q, options: labels.map((l, k) => ({ option_label: l, option_text: q.options?.[k]?.option_text || '', order: k })) };
                                        }),
                                    })));
                                }
                            }}
                            onAdd={() => setAddTestTypeOpen(true)}
                            onDelete={(item) => handleDeleteManageable('Test Type', item)}
                            colors={colors}
                        />
                        </Box>
                    </Box>
                    <ManageableAutocomplete
                        label="Subject"
                        options={subjects}
                        value={subjects.find(s => s.id === subjectId) || null}
                        onChange={(val) => {
                            markDirty();
                            setSubjectId(val?.id || '');
                            // Auto-select matching scoring profile, clear if no match
                            if (val) {
                                const match = scoringProfiles.find(p => p.subject && p.subject.toLowerCase() === val.name.toLowerCase());
                                setScoringProfileId(match ? match.id : '');
                                // Auto-set FRQ submission based on AP exam format
                                const format = getApExamFormat(val.name);
                                if (format === 'hybrid') setFrqSubmissionMethod('upload');
                                else if (format === 'digital') setFrqSubmissionMethod('text');
                            } else {
                                setScoringProfileId('');
                            }
                        }}
                        onAdd={() => { setAddSubjectSource('test'); setAddSubjectOpen(true); }}
                        onDelete={(item) => handleDeleteManageable('Subject', item)}
                        colors={colors}
                    />
                    {/* Test category */}
                    <Box>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={testCategory}
                                label="Category"
                                onChange={(e) => { markDirty(); setTestCategory(e.target.value); }}
                            >
                                <MenuItem value="regular">Regular</MenuItem>
                                <MenuItem value="midterm">Midterm</MenuItem>
                                <MenuItem value="final">Final</MenuItem>
                                <MenuItem value="source">Source (question pool)</MenuItem>
                            </Select>
                        </FormControl>
                        {testCategory === 'source' && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Question pool — its chapter-tagged questions feed the Import / Generate pickers. Not meant to be assigned to students.
                            </Typography>
                        )}
                        {(() => {
                            const t = title.toLowerCase();
                            let warn = null;
                            if (testCategory === 'regular' && (t.includes('midterm') || t.includes('final'))) {
                                warn = `Title contains "${t.includes('midterm') ? 'midterm' : 'final'}" but category is set to "Regular"`;
                            } else if (testCategory === 'midterm' && !t.includes('midterm')) {
                                warn = 'Category is "Midterm" but title doesn\'t contain "midterm"';
                            } else if (testCategory === 'final' && !t.includes('final')) {
                                warn = 'Category is "Final" but title doesn\'t contain "final"';
                            }
                            return warn ? (
                                <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                                    ⚠ {warn}
                                </Typography>
                            ) : null;
                        })()}
                    </Box>
                    {/* FRQ toggle + actions — stacked, centered */}
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5} sx={{ flex: 1, alignItems: 'center' }}>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            fullWidth
                            value={frqSubmissionMethod}
                            onChange={(_, v) => { if (v) { markDirty(); setFrqSubmissionMethod(v); } }}
                            sx={{ '& .MuiToggleButton-root': { py: 0.6, fontSize: 12, textTransform: 'none' } }}
                        >
                            <ToggleButton value="text">FRQ: Text</ToggleButton>
                            <ToggleButton value="upload">FRQ: File Upload</ToggleButton>
                        </ToggleButtonGroup>
                        <Autocomplete
                            size="small"
                            options={[
                                ...AP_EXAM_FORMATS.digital.map(s => ({ name: s, format: 'Digital (text)' })),
                                ...AP_EXAM_FORMATS.hybrid.map(s => ({ name: s, format: 'Hybrid (file upload)' })),
                            ]}
                            groupBy={(opt) => opt.format}
                            getOptionLabel={(opt) => opt.name}
                            onChange={(_, val) => {
                                if (!val) return;
                                setFrqSubmissionMethod(val.format.includes('Hybrid') ? 'upload' : 'text');
                            }}
                            renderInput={(params) => <TextField {...params} label="AP Format Lookup" placeholder="Search..." />}
                            ListboxProps={{ sx: { maxHeight: 300 } }}
                            value={null}
                            blurOnSelect
                        />
                        <Button
                            variant="contained"
                            startIcon={<AutoFixHighIcon />}
                            onClick={() => setAiModalOpen(true)}
                            color="info"
                            size="small"
                            fullWidth
                        >
                            AI Auto-fill
                        </Button>
                        <FormControlLabel
                            control={<Checkbox size="small" checked={releaseGrades} onChange={(e) => { markDirty(); setReleaseGrades(e.target.checked); }} />}
                            label={<Typography variant="body2">Release Grades</Typography>}
                            sx={{ justifyContent: 'center' }}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<UploadFileIcon />}
                            endIcon={<ExpandMoreIcon />}
                            onClick={(e) => setImportMenuAnchor(e.currentTarget)}
                            title="Copy questions from existing tests or chapters into a section"
                            fullWidth
                        >
                            Import Questions
                        </Button>
                        <Menu anchorEl={importMenuAnchor} open={Boolean(importMenuAnchor)} onClose={() => setImportMenuAnchor(null)}>
                            <MenuItem onClick={() => { setImportMenuAnchor(null); setImportTarget('new'); }}>
                                <AddIcon fontSize="small" sx={{ mr: 1 }} /> New section
                            </MenuItem>
                            {sections.map((s, i) => (
                                <MenuItem key={i} onClick={() => { setImportMenuAnchor(null); setImportTarget(i); }}>
                                    Section {i + 1}{s.section_name && s.section_name !== `Section ${i + 1}` ? ` — ${s.section_name}` : ''}
                                </MenuItem>
                            ))}
                        </Menu>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AutoFixHighIcon />}
                            onClick={() => setGenerateOpen(true)}
                            title="Pick chapters and question counts — a random draw builds the test for you"
                            fullWidth
                        >
                            Generate from Chapters
                        </Button>
                        {/* Retake / subset / normalize — only for the Vocab test type. */}
                        {testTypes.find((t) => t.id === testTypeId)?.name === 'Vocab' && (
                            <>
                                <FormControlLabel
                                    control={<Checkbox size="small" checked={allowRetake} onChange={(e) => { markDirty(); setAllowRetake(e.target.checked); }} />}
                                    label={<Typography variant="body2">Allow unlimited retakes</Typography>}
                                    sx={{ justifyContent: 'center' }}
                                />
                                {allowRetake && (() => {
                                    // Only offer subset sizes that divide the pool evenly, so each
                                    // retake cycle exhausts the pool with no leftover tail.
                                    const pool = sections.reduce((n, s) => n + (s.questions?.length || 0), 0);
                                    const opts = perAttemptOptions(pool);
                                    const cur = (questionsPerAttempt === '' || questionsPerAttempt == null) ? '' : String(questionsPerAttempt);
                                    const curIsValid = cur === '' || opts.map(String).includes(cur);
                                    return (
                                        <FormControl size="small" sx={{ mt: 0.5, minWidth: 240 }} error={!curIsValid}>
                                            <InputLabel>Questions per attempt</InputLabel>
                                            <Select
                                                label="Questions per attempt"
                                                value={cur}
                                                onChange={(e) => { markDirty(); setQuestionsPerAttempt(e.target.value); }}
                                            >
                                                <MenuItem value="">Whole test{pool ? ` (${pool})` : ''}</MenuItem>
                                                {!curIsValid && cur !== '' && (
                                                    <MenuItem value={cur}>{cur} — doesn’t divide {pool}</MenuItem>
                                                )}
                                                {opts.map((d) => (
                                                    <MenuItem key={d} value={String(d)}>{d} of {pool}</MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                {curIsValid
                                                    ? 'Random subset each attempt — no repeats until the pool is exhausted'
                                                    : `Pick a size that divides ${pool} evenly (no leftover words)`}
                                            </FormHelperText>
                                        </FormControl>
                                    );
                                })()}
                                <FormControlLabel
                                    control={<Checkbox size="small" checked={normalizeShortAnswers} onChange={(e) => { markDirty(); setNormalizeShortAnswers(e.target.checked); }} />}
                                    label={<Typography variant="body2">Normalize short answers (case/accents/spacing)</Typography>}
                                    sx={{ justifyContent: 'center' }}
                                />
                            </>
                        )}
                    </Box>
                    </Box>

                    {/* Right Column */}
                    <Box display="flex" flexDirection="column" gap={2} sx={{ minWidth: 0 }}>
                    <Autocomplete
                        fullWidth
                        options={folders}
                        value={folders.find(f => f.id === folderId) || null}
                        onChange={(_, val) => { markDirty(); setFolderId(val?.id || ''); }}
                        getOptionLabel={(opt) => opt.name}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options;
                            const lower = inputValue.toLowerCase();

                            const includeIndices = new Set();
                            options.forEach((o, i) => {
                                if (o.name.toLowerCase().includes(lower)) {
                                    includeIndices.add(i);
                                    let targetDepth = o.depth - 1;
                                    for (let j = i - 1; j >= 0 && targetDepth >= 0; j--) {
                                        if (options[j].depth === targetDepth) {
                                            includeIndices.add(j);
                                            targetDepth--;
                                        }
                                    }
                                    for (let j = i + 1; j < options.length && options[j].depth > o.depth; j++) {
                                        includeIndices.add(j);
                                    }
                                }
                            });

                            return options.filter((_, i) => includeIndices.has(i));
                        }}
                        renderOption={(props, opt) => (
                            <li {...props} key={opt.id} style={opt.depth === 0 ? { borderTop: `1px solid ${colors.grey[700]}` } : {}}>
                                <Typography sx={{ whiteSpace: 'pre', fontWeight: opt.depth === 0 ? 700 : 400 }}>
                                    {opt.label}
                                </Typography>
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Folder" />}
                        ListboxProps={{ sx: { maxHeight: 300 } }}
                    />
                    <ManageableAutocomplete
                            label="Scoring Profile"
                            options={(() => {
                                const selectedSubject = subjects.find(s => s.id === subjectId);
                                const all = scoringProfiles.map(p => ({ ...p, label: p.name }));
                                if (!selectedSubject) return all;
                                // Matching subject first, then the rest
                                const matching = all.filter(p => p.subject && p.subject.toLowerCase() === selectedSubject.name.toLowerCase());
                                const rest = all.filter(p => !p.subject || p.subject.toLowerCase() !== selectedSubject.name.toLowerCase());
                                if (matching.length > 0 && rest.length > 0) {
                                    return [...matching.map(p => ({ ...p, label: `★ ${p.name}` })), ...rest];
                                }
                                return all;
                            })()}
                            value={scoringProfiles.find(p => p.id === scoringProfileId) ? { ...scoringProfiles.find(p => p.id === scoringProfileId), label: scoringProfiles.find(p => p.id === scoringProfileId).name } : null}
                            onChange={(val) => { markDirty(); setScoringProfileId(val?.id || ''); }}
                            onAdd={() => { resetProfileForm(); setAddProfileOpen(true); }}
                            onDelete={(item) => handleDeleteManageable('Scoring Profile', item)}
                            addLabel="Add New Profile"
                            colors={colors}
                        />
                    {/* Scoring Profile Preview */}
                    <Box sx={{ p: 1.5, borderRadius: 1, border: `1px solid ${colors.grey[700]}`, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', mt: 1, height: 160, overflowY: 'auto' }}>
                        {(() => {
                            const sp = scoringProfiles.find(p => p.id === scoringProfileId);
                            if (!sp) return (
                                <Box display="flex" alignItems="center" justifyContent="center" sx={{ height: '100%', minHeight: 60 }}>
                                    <Typography variant="body2" color="text.secondary">Select a scoring profile to view preview</Typography>
                                </Box>
                            );
                            const sc = sp.score_conversion || {};
                            const scEntries = Object.entries(sc).sort(([a], [b]) => b - a);
                            const frqMults = sp.metadata?.frq_multipliers || [];
                            const cardSx = { textAlign: 'center', px: 1, py: 0.4, borderRadius: 1, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' };

                            return (
                                <>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="caption" fontWeight={700}>{sp.name}</Typography>
                                        <Tooltip title="Edit Scoring Profile">
                                            <IconButton size="small" onClick={() => openEditProfile(scoringProfileId)} sx={{ p: 0.3 }}>
                                                <SettingsIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Box display="flex" flexWrap="wrap" gap={0.5} mb={scEntries.length > 0 ? 1 : 0}>
                                        <Box sx={cardSx}>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>MCQ</Typography>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>×{sp.mcq_multiplier || '—'}</Typography>
                                        </Box>
                                        {sp.metadata?.frq_default_multiplier != null && (
                                            <Box sx={cardSx}>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700 }}>FRQ Default</Typography>
                                                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>×{sp.metadata.frq_default_multiplier}</Typography>
                                            </Box>
                                        )}
                                        {Array.isArray(sp.frq_targets) && sp.frq_targets.length > 0 && (
                                            <Box sx={cardSx}>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700 }}>FRQ Targets</Typography>
                                                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{sp.frq_targets.join(', ')}</Typography>
                                            </Box>
                                        )}
                                        {frqMults.map((fq, i) => (
                                            <Box key={i} sx={cardSx}>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
                                                    {fq.type ? (QUESTION_TYPES.find(t => t.value === fq.type)?.label || fq.type.toUpperCase()) : `Q${fq.question}`}
                                                </Typography>
                                                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>×{fq.multiplier}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                    {scEntries.length > 0 && (
                                        <Box display="flex" gap={0.5}>
                                            {scEntries.map(([grade, range]) => (
                                                <Box key={grade} sx={{ flex: 1, textAlign: 'center', py: 0.3, borderRadius: 1, border: `1px solid ${colors.grey[700]}` }}>
                                                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{grade}</Typography>
                                                    <Typography sx={{ fontSize: 10 }} color="text.secondary">{range[0]}–{range[1]}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </>
                            );
                        })()}
                    </Box>
                    </Box>
                </Box>
            </Paper>

            {/* Sections */}
            {sections.map((section, sIdx) => (
                <Accordion key={sIdx} defaultExpanded sx={{ bgcolor: colors.primary[600], mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box display="flex" alignItems="center" gap={2} width="100%">
                            <Typography fontWeight={600}>
                                {section.section_name || `Section ${section.section_number}`}
                            </Typography>
                            <Chip label={`${section.questions.length} Q`} size="small" />
                            <Chip label={`${calculateSectionScore(section.questions)} pts`} size="small" variant="outlined" />
                            {(
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeSection(sIdx); }} sx={{ ml: 'auto' }}>
                                    <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box display="flex" gap={2} mb={2} alignItems="center">
                            <TextField
                                label="Section Name"
                                size="small"
                                value={section.section_name}
                                onChange={(e) => updateSection(sIdx, 'section_name', e.target.value)}
                                sx={{ width: 300 }}
                            />
                            <TextField
                                label="Q# Offset"
                                size="small"
                                type="number"
                                value={section._offset ?? 0}
                                onChange={(e) => {
                                    const offset = parseInt(e.target.value) || 0;
                                    updateSection(sIdx, '_offset', offset);
                                    // Auto-renumber questions based on offset
                                    setSections(prev => prev.map((s, i) => {
                                        if (i !== sIdx) return s;
                                        return {
                                            ...s,
                                            _offset: offset,
                                            questions: s.questions.map((q, qi) => ({
                                                ...q,
                                                question_number: offset + qi + 1,
                                            })),
                                        };
                                    }));
                                }}
                                sx={{ width: 100, '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' }, '& input[type=number]': { MozAppearance: 'textfield' } }}
                                inputProps={{ min: 0 }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={section._actStyle ?? false}
                                        onChange={(e) => {
                                            const actStyle = e.target.checked;
                                            updateSection(sIdx, '_actStyle', actStyle);
                                            // Re-label all MCQ questions in this section
                                            const ACT_ODD = ['A', 'B', 'C', 'D', 'E'];
                                            const ACT_EVEN = ['F', 'G', 'H', 'J', 'K'];
                                            setSections(prev => prev.map((s, i) => {
                                                if (i !== sIdx) return s;
                                                return {
                                                    ...s,
                                                    _actStyle: actStyle,
                                                    questions: s.questions.map(q => {
                                                        if (q.question_type !== 'mcq') return q;
                                                        const numChoices = q.options?.length || 4;
                                                        const isOdd = q.question_number % 2 === 1;
                                                        const labels = actStyle
                                                            ? (isOdd ? ACT_ODD : ACT_EVEN).slice(0, numChoices)
                                                            : Array.from({ length: numChoices }, (_, k) => String.fromCharCode(65 + k));
                                                        return {
                                                            ...q,
                                                            options: labels.map((l, k) => ({ option_label: l, option_text: q.options?.[k]?.option_text || '', order: k })),
                                                        };
                                                    }),
                                                };
                                            }));
                                        }}
                                    />
                                }
                                label={<Typography variant="caption">ACT-style</Typography>}
                            />
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Tabs: Questions | Answers (only when Question UI is on) */}
                        {useQuestionUi && (
                            <Tabs
                                value={sectionTab(sIdx)}
                                onChange={(_, v) => setSectionTabMap(prev => ({ ...prev, [sIdx]: v }))}
                                sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none' } }}
                            >
                                <Tab label="Answers" value="answers" />
                                <Tab label="Questions" value="questions" />
                            </Tabs>
                        )}

                        {/* Questions tab — editable question content + live preview */}
                        {useQuestionUi && sectionTab(sIdx) === 'questions' && (() => {
                            const Q_PAGE_SIZE = 20;
                            const qTotal = section.questions.length;
                            const qPageCount = Math.max(1, Math.ceil(qTotal / Q_PAGE_SIZE));
                            const qPage = Math.min(sectionPageMap[sIdx] || 0, qPageCount - 1);
                            const qStart = qPage * Q_PAGE_SIZE;
                            const qGo = (p) => setSectionPageMap(prev => ({ ...prev, [sIdx]: Math.max(0, Math.min(qPageCount - 1, p)) }));
                            const qPager = () => (
                                <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} my={1}>
                                    <Button size="small" variant="outlined" disabled={qPage <= 0} onClick={() => qGo(qPage - 1)}>Prev</Button>
                                    <Typography variant="caption" color="text.secondary">
                                        Questions {qStart + 1}–{Math.min(qStart + Q_PAGE_SIZE, qTotal)} of {qTotal} · Page {qPage + 1}/{qPageCount}
                                    </Typography>
                                    <Button size="small" variant="outlined" disabled={qPage >= qPageCount - 1} onClick={() => qGo(qPage + 1)}>Next</Button>
                                </Box>
                            );
                            return (
                            <Box>
                                {qPageCount > 1 && qPager()}
                                {section.questions.slice(qStart, qStart + Q_PAGE_SIZE).map((q, qLocalIdx) => {
                                    const qIdx = qStart + qLocalIdx;
                                    const sliced = section.questions.slice(qStart, qStart + Q_PAGE_SIZE);
                                    const prevQ = qLocalIdx > 0 ? sliced[qLocalIdx - 1] : null;
                                    const group = q.group_ref ? (section.question_groups || []).find(g => g._temp_id === q.group_ref) : null;
                                    const showPassage = group && (!prevQ || prevQ.group_ref !== q.group_ref);
                                    const groupSize = group ? section.questions.filter(x => x.group_ref === q.group_ref).length : 0;
                                    return (
                                    <React.Fragment key={qIdx}>
                                    {showPassage && (
                                        <Paper sx={{ p: 2, mb: 1.5, bgcolor: colors.primary[600], borderLeft: `3px solid ${colors.blueAccent[300]}` }}>
                                            <Typography variant="caption" fontWeight={700} color={colors.blueAccent[300]} display="block" mb={1}>
                                                Passage — shared by {groupSize} question{groupSize === 1 ? '' : 's'}
                                            </Typography>
                                            <TextField
                                                multiline minRows={2} maxRows={12} fullWidth size="small"
                                                label="Passage text"
                                                value={group.stimulus_text || ''}
                                                onChange={(e) => updateGroup(sIdx, q.group_ref, { stimulus_text: e.target.value })}
                                            />
                                            {group.stimulus_url && (
                                                <Box component="img" src={group.stimulus_url} alt="passage stimulus" sx={{ maxWidth: '100%', mt: 1 }} />
                                            )}
                                        </Paper>
                                    )}
                                    <Paper sx={{ p: 2, mb: 1.5, bgcolor: colors.primary[700], borderLeft: `3px solid ${colors.greenAccent?.[500] || colors.blueAccent[500]}` }}>
                                        <Box display="flex" alignItems="center" mb={1}>
                                            <Typography variant="caption" fontWeight={700} color={colors.blueAccent[400]}>
                                                Q{q.question_number} — {q.question_type.toUpperCase()}
                                            </Typography>
                                            {dupChipFor(q, sIdx, qIdx, { ml: 1 })}
                                            {splitChipFor(q, sIdx, { ml: 1 })}
                                            {/* Chapter — same per-question tagging as the Answers tab */}
                                            <Box display="flex" gap={1} alignItems="center" sx={{ ml: 2 }}
                                                title={q._chapter_course
                                                    ? `${q._chapter_kind === 'lesson' ? 'Lesson' : q._chapter_kind === 'difficulty' ? 'Difficulty' : 'Chapter'} number (${q._chapter_course}) — empty = no chapter`
                                                    : 'Chapter number — empty = no chapter'}>
                                                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                                    {q._chapter_kind === 'lesson' ? 'Lesson' : q._chapter_kind === 'difficulty' ? 'Difficulty' : 'Chapter'}
                                                </Typography>
                                                {q._chapter_kind === 'difficulty' ? (
                                                    <Box component="select" value={q.chapter_number ?? ''}
                                                        onChange={(e) => setQuestionChapter(sIdx, qIdx, e.target.value === '' ? '' : parseInt(e.target.value))}
                                                        sx={{ ...liteSelectSx, width: 72 }}>
                                                        <option value="">—</option>
                                                        <option value={1}>Adv</option>
                                                        <option value={2}>Int</option>
                                                    </Box>
                                                ) : (
                                                    <Box component="input" type="number" min="1"
                                                        value={q.chapter_number ?? ''}
                                                        onChange={(e) => setQuestionChapter(sIdx, qIdx, e.target.value === '' ? '' : (parseInt(e.target.value) || ''))}
                                                        sx={{ ...liteInputSx, width: 64, textAlign: 'center', '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { display: 'none' }, MozAppearance: 'textfield' }}
                                                    />
                                                )}
                                                {q._chapter_course && (
                                                    <Typography variant="caption" color="text.secondary">({q._chapter_course})</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box display="flex" gap={2}>
                                            {/* Left: editable fields */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <TextField
                                                    label="Question Prompt"
                                                    multiline
                                                    minRows={2}
                                                    maxRows={8}
                                                    fullWidth
                                                    size="small"
                                                    value={q.question_prompt || ''}
                                                    onChange={(e) => {
                                                        markDirty();
                                                        setSections(prev => prev.map((s, si) =>
                                                            si !== sIdx ? s : {
                                                                ...s,
                                                                questions: s.questions.map((qq, qi) =>
                                                                    qi !== qIdx ? qq : { ...qq, question_prompt: e.target.value }
                                                                ),
                                                            }
                                                        ));
                                                    }}
                                                    sx={{ mb: 1 }}
                                                />
                                                {q.question_type === 'mcq' && (
                                                    <>
                                                        {/* Per-option rows: editable label + text */}
                                                        {q.options?.map((opt, oIdx) => (
                                                            <Box key={oIdx} display="flex" gap={1} alignItems="center" mb={0.5}>
                                                                <TextField
                                                                    size="small"
                                                                    value={opt.option_label || ''}
                                                                    onChange={(e) => updateOptionLabel(sIdx, qIdx, oIdx, e.target.value)}
                                                                    sx={{ width: 60 }}
                                                                    inputProps={{ maxLength: 4, style: { textAlign: 'center', fontWeight: 600 } }}
                                                                />
                                                                <TextField
                                                                    size="small"
                                                                    fullWidth
                                                                    placeholder={`Option ${opt.option_label || '?'} text`}
                                                                    value={opt.option_text || ''}
                                                                    onChange={(e) => {
                                                                        markDirty();
                                                                        setSections(prev => prev.map((s, si) =>
                                                                            si !== sIdx ? s : {
                                                                                ...s,
                                                                                questions: s.questions.map((qq, qi) =>
                                                                                    qi !== qIdx ? qq : {
                                                                                        ...qq,
                                                                                        options: qq.options.map((o, oi) =>
                                                                                            oi !== oIdx ? o : { ...o, option_text: e.target.value }
                                                                                        ),
                                                                                    }
                                                                                ),
                                                                            }
                                                                        ));
                                                                    }}
                                                                />
                                                            </Box>
                                                        ))}

                                                        {/* Duplicate label warning */}
                                                        {(() => {
                                                            const dups = findDuplicateLabels(q.options || []);
                                                            if (dups.length === 0) return null;
                                                            return (
                                                                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                                                    ⚠ Duplicate label{dups.length > 1 ? 's' : ''}: {dups.join(', ')}
                                                                </Typography>
                                                            );
                                                        })()}
                                                    </>
                                                )}
                                            </Box>
                                            {/* Right: live preview (memoized — re-renders only when this question changes) */}
                                            <QuestionPreviewPane q={q} bg={colors.primary[800]} />
                                        </Box>
                                    </Paper>
                                    </React.Fragment>
                                    );
                                })}
                                {qPageCount > 1 && qPager()}
                            </Box>
                            );
                        })()}

                        {/* Answers tab (existing content) — show when no Question UI or when Answers tab is active */}
                        <Box sx={{ display: (useQuestionUi && sectionTab(sIdx) === 'questions') ? 'none' : 'block' }}>

                        {/* Questions with DnD */}
                        <DragDropContext onDragEnd={(result) => {
                            if (!result.destination) return;
                            const { source, destination } = result;
                            if (source.index === destination.index) return;
                            setSections(prev => prev.map((s, i) => {
                                if (i !== sIdx) return s;
                                const nums = s.questions.map(q => q.question_number);
                                const reordered = [...s.questions];
                                const [moved] = reordered.splice(source.index, 1);
                                reordered.splice(destination.index, 0, moved);
                                return { ...s, questions: reordered.map((q, qi) => ({ ...q, question_number: nums[qi] })) };
                            }));
                        }}>
                        <Droppable droppableId={`questions-${sIdx}`}>
                        {(provided) => (
                        <Box ref={provided.innerRef} {...provided.droppableProps}>
                        {section.questions.map((q, qIdx) => (
                            <Draggable key={`q-${sIdx}-${qIdx}`} draggableId={`q-${sIdx}-${qIdx}`} index={qIdx}>
                            {(prov, snap) => (
                            <Paper ref={prov.innerRef} {...prov.draggableProps} key={qIdx} sx={{ p: 2, mb: 1.5, bgcolor: colors.primary[700], borderLeft: `3px solid ${colors.blueAccent[500]}`, opacity: snap.isDragging ? 0.7 : 1, display: 'flex', gap: 2 }}>
                                {/* Left: Editor */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                {/* Row 1: drag handle, Q#, Type, type-specific fields, delete */}
                                <Box display="flex" gap={1} alignItems="center">
                                    <Box {...prov.dragHandleProps} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: 'text.secondary', flexShrink: 0 }}>⠿</Box>
                                    <Box component="input" type="number" value={q.question_number}
                                        onChange={(e) => updateQuestion(sIdx, qIdx, 'question_number', e.target.value === '' ? '' : (parseInt(e.target.value) || ''))}
                                        onBlur={(e) => { if (!e.target.value) updateQuestion(sIdx, qIdx, 'question_number', 1); }}
                                        sx={{ ...liteInputSx, width: 55, fontWeight: 700, textAlign: 'center', '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { display: 'none' }, MozAppearance: 'textfield' }}
                                    />
                                    <Box component="select" value={q.question_type}
                                        onChange={(e) => updateQuestion(sIdx, qIdx, 'question_type', e.target.value)}
                                        sx={{ ...liteSelectSx, minWidth: 110 }}
                                    >
                                        {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </Box>
                                    {dupChipFor(q, sIdx, qIdx)}
                                    {splitChipFor(q, sIdx)}

                                    {q.question_type === 'mcq' && (
                                        <>
                                            <Labeled label="answer">
                                            <Box component="input" value={q.answer} placeholder="Ans"
                                                onChange={(e) => updateQuestion(sIdx, qIdx, 'answer', e.target.value.toUpperCase())}
                                                sx={{ ...liteInputSx, width: 50, textAlign: 'center', fontWeight: 600 }}
                                            />
                                            </Labeled>
                                            <Labeled label="options">
                                            <Box component="input" type="number"
                                                value={q._editingChoiceCount ?? (q.options?.length || 4)}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === '') {
                                                        updateQuestion(sIdx, qIdx, '_editingChoiceCount', '');
                                                        return;
                                                    }
                                                    const num = Math.max(1, Math.min(10, parseInt(val) || 1));
                                                    const actStyle = section._actStyle ?? false;
                                                    const ACT_ODD = ['A', 'B', 'C', 'D', 'E'];
                                                    const ACT_EVEN = ['F', 'G', 'H', 'J', 'K'];
                                                    const isOdd = q.question_number % 2 === 1;
                                                    const labels = actStyle
                                                        ? (isOdd ? ACT_ODD : ACT_EVEN).slice(0, num)
                                                        : Array.from({ length: num }, (_, k) => String.fromCharCode(65 + k));
                                                    setSections(prev => prev.map((s, si) =>
                                                        si !== sIdx ? s : {
                                                            ...s,
                                                            questions: s.questions.map((qq, qi) =>
                                                                qi !== qIdx ? qq : {
                                                                    ...qq,
                                                                    _editingChoiceCount: undefined,
                                                                    options: labels.map((l, k) => ({
                                                                        option_label: l,
                                                                        option_text: qq.options?.[k]?.option_text || '',
                                                                        order: k,
                                                                    })),
                                                                }
                                                            ),
                                                        }
                                                    ));
                                                    markDirty();
                                                }}
                                                onBlur={(e) => {
                                                    if (!e.target.value) {
                                                        updateQuestion(sIdx, qIdx, '_editingChoiceCount', undefined);
                                                    }
                                                }}
                                                sx={{ ...liteInputSx, width: 45, textAlign: 'center', '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { display: 'none' }, MozAppearance: 'textfield' }}
                                            />
                                            </Labeled>
                                            {/* Compact label shortcut: single char = sequential from that
                                                letter (E→EFGH); multi-char = each char maps to one option
                                                (AIJKL→A,I,J,K,L). Applied immediately on keystroke. */}
                                            <Labeled label="labels">
                                            <Box component="input"
                                                placeholder="Labels"
                                                value={startLettersByQuestion[`${sIdx}-${qIdx}`] ?? (q.options || []).map(o => o.option_label || '').join('')}
                                                onChange={(e) => {
                                                    const opts = q.options || [];
                                                    const raw = e.target.value.toUpperCase().slice(0, opts.length);
                                                    setStartLettersByQuestion(prev => ({ ...prev, [`${sIdx}-${qIdx}`]: raw }));
                                                    if (!raw) return;
                                                    let newLabels;
                                                    if (raw.length === 1) {
                                                        newLabels = generateLabelsFromStart(raw, opts.length);
                                                    } else {
                                                        newLabels = raw.split('');
                                                    }
                                                    if (newLabels) {
                                                        markDirty();
                                                        updateQuestion(sIdx, qIdx, 'options', opts.map((o, k) => ({
                                                            ...o,
                                                            option_label: newLabels[k] || o.option_label,
                                                        })));
                                                    }
                                                }}
                                                title="Single letter = sequential (E→EFGH); multiple = per-option (AIJKL)"
                                                sx={{ ...liteInputSx, width: 70, textAlign: 'center', fontWeight: 600 }}
                                            />
                                            </Labeled>
                                            {/* Per-question points — grading already honors mcq max_points;
                                                this just exposes it (default 1) */}
                                            <Labeled label="pts">
                                            <Box component="input" type="number" min="1"
                                                placeholder="pts"
                                                value={q.max_points ?? 1}
                                                onChange={(e) => updateQuestion(sIdx, qIdx, 'max_points', e.target.value === '' ? '' : (parseInt(e.target.value) || ''))}
                                                onBlur={(e) => { if (!e.target.value) updateQuestion(sIdx, qIdx, 'max_points', 1); }}
                                                title="Points for this question (default 1)"
                                                sx={{ ...liteInputSx, width: 45, textAlign: 'center', '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { display: 'none' }, MozAppearance: 'textfield' }}
                                            />
                                            </Labeled>
                                        </>
                                    )}
                                    {['saq', 'dbq', 'leq'].includes(q.question_type) && (() => {
                                        const hasParts = q.parts && q.parts.length > 0;
                                        const getLeafSum = (parts) => {
                                            let sum = 0;
                                            for (const p of parts || []) {
                                                if (p.subparts && p.subparts.length > 0) sum += getLeafSum(p.subparts);
                                                else sum += parseFloat(p.max_points) || 0;
                                            }
                                            return sum;
                                        };
                                        return (
                                            <Labeled label="pts">
                                            <Box component="input" type="number"
                                                placeholder="pts"
                                                value={hasParts ? getLeafSum(q.parts) : (q.max_points ?? 1)}
                                                readOnly={hasParts}
                                                onChange={hasParts ? undefined : (e) => updateQuestion(sIdx, qIdx, 'max_points', e.target.value === '' ? '' : (parseInt(e.target.value) || ''))}
                                                onBlur={hasParts ? undefined : (e) => { if (!e.target.value) updateQuestion(sIdx, qIdx, 'max_points', 1); }}
                                                sx={{ ...liteInputSx, width: 55, textAlign: 'center', opacity: hasParts ? 0.6 : 1, cursor: hasParts ? 'default' : undefined, '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { display: 'none' }, MozAppearance: 'textfield' }}
                                                title={hasParts ? `Computed from ${q.parts.length} part(s)` : 'Max points for this question'}
                                            />
                                            </Labeled>
                                        );
                                    })()}

                                    {/* multi_answer AND grid_in both grade against accepted_answers
                                        (grid-in accepts numeric equivalents: "1/4" ≡ ".25" ≡ "0.25") */}
                                    {['multi_answer', 'grid_in'].includes(q.question_type) && (() => {
                                        const answers = (() => { try { const p = typeof q.accepted_answers === 'string' ? JSON.parse(q.accepted_answers) : q.accepted_answers; return Array.isArray(p) ? p : []; } catch { return []; } })();
                                        const removeAnswer = (idx) => {
                                            const next = answers.filter((_, i) => i !== idx);
                                            updateQuestion(sIdx, qIdx, 'accepted_answers', next.length > 0 ? JSON.stringify(next) : '');
                                        };
                                        return (
                                            <Box sx={{ flex: 1, minWidth: 150, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                                                {answers.map((a, i) => (
                                                    <Chip key={i} label={a} size="small" onDelete={() => removeAnswer(i)}
                                                        sx={{ height: 22, fontSize: 12 }}
                                                    />
                                                ))}
                                                <Box component="input" placeholder={answers.length ? 'Add more...' : 'Type answer, press Enter'}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === '|') {
                                                            e.preventDefault();
                                                            const val = e.target.value.trim();
                                                            if (val) {
                                                                // Split by pipe in case pasted
                                                                const newAnswers = val.split('|').map(s => s.trim()).filter(Boolean);
                                                                updateQuestion(sIdx, qIdx, 'accepted_answers', JSON.stringify([...answers, ...newAnswers]));
                                                                e.target.value = '';
                                                            }
                                                        }
                                                        if (e.key === 'Backspace' && !e.target.value && answers.length > 0) {
                                                            removeAnswer(answers.length - 1);
                                                        }
                                                    }}
                                                    sx={{ ...liteInputSx, flex: 1, minWidth: 80, border: 'none', outline: 'none', '&:focus': { border: 'none' } }}
                                                />
                                            </Box>
                                        );
                                    })()}

                                    {/* Chapter number — per-question; course resolves from the
                                        question itself, its siblings, or a one-time dialog.
                                        Same above-the-box caption style as answer/options/pts. */}
                                    <Box sx={{ flexShrink: 0 }}
                                        title={q._chapter_course
                                            ? `${q._chapter_kind === 'lesson' ? 'Lesson' : q._chapter_kind === 'difficulty' ? 'Difficulty' : 'Chapter'} number (${q._chapter_course}) — empty = no chapter`
                                            : 'Chapter number — empty = no chapter'}>
                                        <Labeled label={q._chapter_kind === 'lesson' ? 'lesson' : q._chapter_kind === 'difficulty' ? 'diff' : 'ch'}>
                                        {q._chapter_kind === 'difficulty' ? (
                                            <Box component="select" value={q.chapter_number ?? ''}
                                                onChange={(e) => setQuestionChapter(sIdx, qIdx, e.target.value === '' ? '' : parseInt(e.target.value))}
                                                sx={{ ...liteSelectSx, width: 64 }}>
                                                <option value="">—</option>
                                                <option value={1}>Adv</option>
                                                <option value={2}>Int</option>
                                            </Box>
                                        ) : (
                                            <Box component="input" type="number" min="1"
                                                value={q.chapter_number ?? ''}
                                                onChange={(e) => setQuestionChapter(sIdx, qIdx, e.target.value === '' ? '' : (parseInt(e.target.value) || ''))}
                                                sx={{ ...liteInputSx, width: 48, textAlign: 'center', '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { display: 'none' }, MozAppearance: 'textfield' }}
                                            />
                                        )}
                                        </Labeled>
                                    </Box>
                                    <Box component="button" onClick={() => removeQuestion(sIdx, qIdx)}
                                        sx={{ ml: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'error.main', p: 0.5, display: 'flex', '&:hover': { opacity: 0.7 } }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </Box>
                                </Box>

                                {/* FRQ Parts + Choice Group */}
                                {['saq', 'dbq', 'leq'].includes(q.question_type) && (
                                    <Box mt={1}>
                                        <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Choice Group:</Typography>
                                            <Box component="input"
                                                type="number"
                                                min="1"
                                                placeholder="e.g. 1"
                                                value={q.choice_group || ''}
                                                onChange={(e) => updateQuestion(sIdx, qIdx, 'choice_group', e.target.value ? parseInt(e.target.value) : '')}
                                                sx={{ ...liteInputSx, width: 50, textAlign: 'center', '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { display: 'none' }, MozAppearance: 'textfield' }}
                                            />
                                            {q.choice_group && (() => {
                                                const siblings = section.questions.filter(sq => sq.choice_group === q.choice_group);
                                                const nums = siblings.map(sq => sq.question_number).join(', ');
                                                if (siblings.length < 2) {
                                                    return <Typography variant="caption" color="error">Need at least 2 questions in choice group {q.choice_group}</Typography>;
                                                }
                                                return <Typography variant="caption" color="text.secondary">(student picks one among Q{nums})</Typography>;
                                            })()}
                                            {(() => {
                                                const getLeafSum = (parts) => {
                                                    let sum = 0;
                                                    for (const p of parts || []) {
                                                        if (p.subparts && p.subparts.length > 0) sum += getLeafSum(p.subparts);
                                                        else sum += parseFloat(p.max_points) || 0;
                                                    }
                                                    return sum;
                                                };
                                                const qTotal = (q.parts && q.parts.length > 0)
                                                    ? getLeafSum(q.parts)
                                                    : (parseFloat(q.max_points) || 0);
                                                return (
                                                    <Chip
                                                        label={`${qTotal} pts`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ ml: 'auto', fontSize: 11, height: 22 }}
                                                    />
                                                );
                                            })()}
                                        </Box>
                                        <PartTree
                                            parts={q.parts}
                                            onChange={(newParts) => updateQuestion(sIdx, qIdx, 'parts', newParts)}
                                            depth={0}
                                        />
                                    </Box>
                                )}
                                </Box>
                                {/* Right: Inline Preview */}
                                <Box sx={{ width: '35%', flexShrink: 0, pl: 2, borderLeft: `1px solid ${colors.grey[700]}` }}>
                                    <span style={{ fontSize: 12, fontWeight: 700 }}>{q.question_number}.</span>

                                    {q.question_type === 'mcq' && q.options?.length > 0 && (
                                        <Box display="flex" gap={1.5} flexWrap="wrap" mt={0.5}>
                                            {q.options.map((opt, i) => {
                                                const correct = opt.option_label === q.answer;
                                                return <span key={i} style={{ fontSize: 11, fontWeight: correct ? 700 : 400, color: correct ? '#66bb6a' : undefined }}>{correct ? '● ' : '○ '}{opt.option_label}</span>;
                                            })}
                                        </Box>
                                    )}

                                    {(q.question_type === 'multi_answer' || q.question_type === 'sa') && (() => {
                                        let answers = [];
                                        try { answers = q.accepted_answers ? JSON.parse(q.accepted_answers) : []; } catch { }
                                        return answers.length > 0
                                            ? <span style={{ fontSize: 11, color: '#66bb6a', fontWeight: 700 }}>Ans: {answers.join(', ')}</span>
                                            : <span style={{ fontSize: 10, color: '#999', fontStyle: 'italic' }}>No answer set</span>;
                                    })()}

                                    {['saq', 'dbq', 'leq'].includes(q.question_type) && (
                                        q.parts?.length > 0 ? (
                                            <PreviewParts parts={q.parts} questionType={q.question_type} depth={0} colors={colors} isUpload={frqSubmissionMethod === 'upload'} />
                                        ) : (
                                            <Box sx={{ mt: 0.5 }}>
                                                <span style={{ fontSize: 10, color: '#999', fontStyle: 'italic' }}>
                                                    {frqSubmissionMethod === 'upload' ? '📎 Upload file...' : q.question_type === 'saq' ? 'Short answer...' : 'Essay response...'}
                                                </span>
                                                <span style={{ fontSize: 10, color: '#999', fontWeight: 600, marginLeft: 8 }}>
                                                    {q.max_points || 1} pt{(parseFloat(q.max_points) || 1) !== 1 ? 's' : ''}
                                                </span>
                                            </Box>
                                        )
                                    )}
                                </Box>
                            </Paper>
                            )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                        </Box>
                        )}
                        </Droppable>
                        </DragDropContext>

                        {(() => {
                            const addType = section._addType || (QUESTION_TYPES.some(t => t.value === section.section_type) ? section.section_type : 'mcq');
                            const addCount = section._addCount ?? 1;
                            const addChoices = section._addChoices ?? 4;
                            const updateAddField = (field, value) => updateSection(sIdx, field, value);

                            const ACT_ODD = ['A', 'B', 'C', 'D', 'E'];
                            const ACT_EVEN = ['F', 'G', 'H', 'J', 'K'];

                            return (
                                <Box display="flex" gap={1} alignItems="center">
                                    <FormControl size="small" sx={{ minWidth: 130 }}>
                                        <Select value={addType} onChange={(e) => updateAddField('_addType', e.target.value)}>
                                            {QUESTION_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    {addType === 'mcq' && (
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Choices"
                                            value={addChoices}
                                            onChange={(e) => updateAddField('_addChoices', parseInt(e.target.value) || 4)}
                                            sx={{ width: 80 }}
                                            inputProps={{ min: 2, max: 10 }}
                                        />
                                    )}
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={addCount}
                                        onChange={(e) => updateAddField('_addCount', parseInt(e.target.value) || 1)}
                                        sx={{ width: 60 }}
                                        inputProps={{ min: 1 }}
                                    />
                                    <Button
                                        startIcon={<AddIcon />}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            setSections(prev => prev.map((s, i) => {
                                                if (i !== sIdx) return s;
                                                const offset = s._offset || 0;
                                                const startNum = offset + s.questions.length + 1;
                                                const count = (s._addCount ?? 1);
                                                const type = s._addType || (QUESTION_TYPES.some(t => t.value === s.section_type) ? s.section_type : 'mcq');
                                                const choices = s._addChoices ?? 4;
                                                const actStyle = s._actStyle ?? false;
                                                const newQs = Array.from({ length: count }, (_, j) => {
                                                    const qNum = startNum + j;
                                                    const isOdd = qNum % 2 === 1;
                                                    const labels = (type === 'mcq' && actStyle)
                                                        ? (isOdd ? ACT_ODD : ACT_EVEN).slice(0, choices)
                                                        : Array.from({ length: choices }, (_, k) => String.fromCharCode(65 + k));
                                                    return {
                                                        ...createEmptyQuestion(qNum, type),
                                                        options: type === 'mcq'
                                                            ? labels.map((l, k) => ({ option_label: l, option_text: '', order: k }))
                                                            : [],
                                                    };
                                                });
                                                return { ...s, questions: [...s.questions, ...newQs] };
                                            }));
                                        }}
                                    >
                                        {(addCount > 1) ? `Add ${addCount} Questions` : 'Add Question'}
                                    </Button>
                                    <Button
                                        startIcon={<UploadFileIcon />}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setImportTarget(sIdx)}
                                        title="Copy questions from existing tests or by chapter into this section"
                                    >
                                        Import Questions
                                    </Button>
                                </Box>
                            );
                        })()}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ))}

            <Box display="flex" gap={2} mt={2} alignItems="center">
                <Button startIcon={<AddIcon />} onClick={addSection} variant="outlined">
                    Add Section
                </Button>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{
                        color: '#51BE90', fontWeight: 700, whiteSpace: 'nowrap',
                        opacity: savedFlash ? 1 : 0, transition: 'opacity .6s ease',
                    }}>
                        Test saved ✓
                    </Typography>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving || anyUploading}
                    >
                        {saving ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save Test' : 'Add Test')}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setPreviewOpen(true)}
                    >
                        Preview
                    </Button>
                </Box>
            </Box>

            {/* Add Scoring Profile Dialog */}
            <Dialog open={addProfileOpen} onClose={() => { setAddProfileOpen(false); resetProfileForm(); }} maxWidth="sm" fullWidth>
                <DialogTitle>{editingProfileId ? 'Edit Scoring Profile' : 'Add New Scoring Profile'}</DialogTitle>
                <DialogContent>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={1}>
                        <TextField
                            autoFocus
                            label="Profile Name"
                            value={newProfileName}
                            onChange={(e) => setNewProfileName(e.target.value)}
                            required
                            placeholder="e.g. AP Biology 2025"
                        />
                        <ManageableAutocomplete
                            label="Subject"
                            options={subjects}
                            value={subjects.find(s => s.name === newProfileSubject || s.label === newProfileSubject) || null}
                            onChange={(val) => setNewProfileSubject(val?.name || '')}
                            onAdd={() => { setAddSubjectSource('profile'); setAddSubjectOpen(true); }}
                            onDelete={(item) => handleDeleteManageable('Subject', item)}
                            colors={colors}
                        />
                        <TextField
                            label="MCQ Multiplier"
                            type="number"
                            value={newProfileMcqMultiplier}
                            onChange={(e) => setNewProfileMcqMultiplier(e.target.value)}
                            placeholder="e.g. 1.0344"
                            inputProps={{ step: 0.0001 }}
                        />
                        <TextField
                            label="FRQ Default Multiplier"
                            type="number"
                            value={newProfileFrqDefaultMultiplier}
                            onChange={(e) => setNewProfileFrqDefaultMultiplier(e.target.value)}
                            placeholder="e.g. 1.4285"
                            inputProps={{ step: 0.0001 }}
                        />
                        <TextField
                            label="FRQ Target Points"
                            value={newProfileFrqTargets}
                            onChange={(e) => setNewProfileFrqTargets(e.target.value)}
                            placeholder="e.g. 10, 12, 10, 8"
                            helperText="From AP scoring worksheet — comma-separated target per FRQ question. Multipliers are derived automatically."
                            fullWidth
                        />
                    </Box>

                    <Typography variant="subtitle2" mt={3} mb={1} fontWeight={600}>
                        FRQ Multipliers
                    </Typography>
                    {newProfileFrqMults.map((m, i) => (
                        <Box key={i} display="flex" gap={0.5} mb={0.5} alignItems="center">
                            <FormControl size="small" sx={{ width: 110 }}>
                                <Select
                                    value={m._mode || 'type'}
                                    onChange={(e) => {
                                        const updated = [...newProfileFrqMults];
                                        updated[i] = { ...updated[i], _mode: e.target.value, type: undefined, question: undefined };
                                        setNewProfileFrqMults(updated);
                                    }}
                                >
                                    <MenuItem value="type">By Type</MenuItem>
                                    <MenuItem value="question">By Q#</MenuItem>
                                </Select>
                            </FormControl>
                            {m._mode === 'question' ? (
                                <TextField
                                    size="small"
                                    value={m.question || ''}
                                    onChange={(e) => {
                                        const updated = [...newProfileFrqMults];
                                        updated[i] = { ...updated[i], question: parseInt(e.target.value) || undefined };
                                        setNewProfileFrqMults(updated);
                                    }}
                                    sx={{ flex: 1 }}
                                    type="number"
                                    inputProps={{ style: { fontWeight: 600 } }}
                                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary', fontWeight: 600 }}>Q</Typography> }}
                                />
                            ) : (
                                <FormControl size="small" sx={{ flex: 1 }}>
                                    <Select
                                        value={m.type || ''}
                                        displayEmpty
                                        onChange={(e) => {
                                            const updated = [...newProfileFrqMults];
                                            updated[i] = { ...updated[i], type: e.target.value || undefined };
                                            setNewProfileFrqMults(updated);
                                        }}
                                    >
                                        <MenuItem value="" disabled><em>Select type</em></MenuItem>
                                        {QUESTION_TYPES.filter(t => t.value !== 'mcq').map(t => (
                                            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            <TextField
                                size="small"
                                value={m.multiplier ?? ''}
                                onChange={(e) => {
                                    const updated = [...newProfileFrqMults];
                                    updated[i] = { ...updated[i], multiplier: parseFloat(e.target.value) || 0 };
                                    setNewProfileFrqMults(updated);
                                }}
                                sx={{ width: 120 }}
                                type="number"
                                inputProps={{ step: 0.0001 }}
                                InputProps={{ startAdornment: <Typography sx={{ mr: 0.3, color: 'text.secondary', fontWeight: 600 }}>×</Typography> }}
                            />
                            <IconButton size="small" onClick={() => setNewProfileFrqMults(prev => prev.filter((_, j) => j !== i))}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>
                    ))}
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setNewProfileFrqMults(prev => [...prev, { _mode: 'type', multiplier: 1 }])}
                        sx={{ mt: 0.5 }}
                    >
                        Add
                    </Button>

                    <Typography variant="subtitle2" mt={3} mb={1} fontWeight={600}>
                        Score Conversion (composite score ranges → AP score 1-5)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" mb={1} display="block">
                        Leave blank if not applicable (non-AP tests)
                    </Typography>
                    {newProfileConversion.map((c, i) => (
                        <Box key={i} display="flex" gap={1} mb={1} alignItems="center">
                            <Chip label={c.score} sx={{ minWidth: 32, fontWeight: 700 }} />
                            <TextField
                                size="small"
                                label="Min"
                                type="number"
                                value={c.min}
                                onChange={(e) => {
                                    const updated = [...newProfileConversion];
                                    updated[i] = { ...updated[i], min: e.target.value };
                                    setNewProfileConversion(updated);
                                }}
                                sx={{ width: 90 }}
                            />
                            <Typography>—</Typography>
                            <TextField
                                size="small"
                                label="Max"
                                type="number"
                                value={c.max}
                                onChange={(e) => {
                                    const updated = [...newProfileConversion];
                                    updated[i] = { ...updated[i], max: e.target.value };
                                    setNewProfileConversion(updated);
                                }}
                                sx={{ width: 90 }}
                            />
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setAddProfileOpen(false); resetProfileForm(); }}>Cancel</Button>
                    <Button onClick={handleSaveProfile} variant="contained" color="secondary" disabled={!newProfileName.trim()}>
                        {editingProfileId ? 'Save' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Test Type Dialog */}
            <Dialog open={addTestTypeOpen} onClose={() => setAddTestTypeOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Add New Test Type</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Type Name"
                        value={newTestTypeName}
                        onChange={(e) => setNewTestTypeName(e.target.value)}
                        onKeyDown={async (e) => {
                            if (e.key === 'Enter' && newTestTypeName.trim()) {
                                try {
                                    const res = await api.post('/test-types', { name: newTestTypeName.trim(), label: newTestTypeName.trim() });
                                    setTestTypes(prev => [...prev, res.data.data]);
                                    setTestTypeId(res.data.data.id);
                                    setNewTestTypeName('');
                                    setAddTestTypeOpen(false);
                                } catch (err) { alert(err.response?.data?.message || 'Failed'); }
                            }
                        }}
                        sx={{ mt: 1 }}
                        placeholder="e.g. AP, SAT, ACT, Competition"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setAddTestTypeOpen(false); setNewTestTypeName(''); }}>Cancel</Button>
                    <Button onClick={async () => {
                        if (!newTestTypeName.trim()) return;
                        try {
                            const res = await api.post('/test-types', { name: newTestTypeName.trim(), label: newTestTypeName.trim() });
                            setTestTypes(prev => [...prev, res.data.data]);
                            setTestTypeId(res.data.data.id);
                            setNewTestTypeName('');
                            setAddTestTypeOpen(false);
                        } catch (err) { alert(err.response?.data?.message || 'Failed'); }
                    }} variant="contained" color="secondary">Add</Button>
                </DialogActions>
            </Dialog>

            {/* Add Subject Dialog */}
            <Dialog open={addSubjectOpen} onClose={() => setAddSubjectOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Add New Subject</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Subject Name"
                        value={newSubjectName}
                        onChange={(e) => setNewSubjectName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddSubject()}
                        sx={{ mt: 1 }}
                        placeholder="e.g. AP Biology, SAT Math, Science Bee"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setAddSubjectOpen(false); setNewSubjectName(''); }}>Cancel</Button>
                    <Button onClick={handleAddSubject} variant="contained" color="secondary">Add</Button>
                </DialogActions>
            </Dialog>
            {/* Recompute Composites Dialog */}
            <Dialog open={recomputeDialogOpen} onClose={handleRecomputeSkip} maxWidth="xs" fullWidth>
                <DialogTitle>Recompute Scores</DialogTitle>
                <DialogContent>
                    <Typography>
                        Scoring profile changed. Would you like to recompute composite and AP scores for all graded tests using this profile?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRecomputeSkip} color="inherit">
                        Skip
                    </Button>
                    <Button onClick={handleRecomputeComposites} variant="contained" disabled={recomputing}>
                        {recomputing ? 'Recomputing...' : 'Recompute'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview Modal */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={700}>Test Preview</Typography>
                    <IconButton onClick={() => setPreviewOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {/* Summary stats */}
                    <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                        <Chip label={`${sections.reduce((sum, s) => sum + s.questions.length, 0)} Questions`} />
                        {(() => {
                            const types = {};
                            sections.forEach(s => s.questions.forEach(q => { types[q.question_type] = (types[q.question_type] || 0) + 1; }));
                            return Object.entries(types).map(([t, c]) => (
                                <Chip key={t} label={`${c} ${(QUESTION_TYPES.find(qt => qt.value === t)?.label) || t}`} variant="outlined" size="small" />
                            ));
                        })()}
                        <Chip label={`${sections.length} Section${sections.length !== 1 ? 's' : ''}`} variant="outlined" size="small" />
                    </Box>

                    {/* Sections */}
                    {sections.map((section, sIdx) => (
                        <Box key={sIdx} mb={3}>
                            <Typography variant="subtitle1" fontWeight={700} mb={1}>
                                {section.section_name || `Section ${section.section_number}`}
                                <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                                    ({section.questions.length} questions)
                                </Typography>
                            </Typography>

                            {section.questions.map((q, qIdx) => (
                                <Box key={qIdx} sx={{ mb: 1.5, pl: 2, borderLeft: `2px solid ${colors.blueAccent[500]}` }}>
                                    <Typography fontWeight={600} mb={0.5}>Q{q.question_number}</Typography>

                                    {/* MCQ options — horizontal */}
                                    {q.question_type === 'mcq' && q.options?.length > 0 && (
                                        <Box display="flex" gap={1.5} ml={2} flexWrap="wrap">
                                            {q.options.map((opt, i) => (
                                                <Box key={i} display="flex" alignItems="center" gap={0.3}>
                                                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${opt.option_label === q.answer ? theme.palette.success.main : colors.grey[500]}`, bgcolor: opt.option_label === q.answer ? theme.palette.success.main : 'transparent' }} />
                                                    <Typography variant="body2" fontWeight={opt.option_label === q.answer ? 700 : 400}>
                                                        {opt.option_label}{opt.option_text ? `: ${opt.option_text}` : ''}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {/* Multi-Answer / SA: show accepted answers */}
                                    {(q.question_type === 'multi_answer' || q.question_type === 'sa') && (() => {
                                        let answers = [];
                                        try { answers = q.accepted_answers ? JSON.parse(q.accepted_answers) : []; } catch (e) { console.log('[Preview] parse error:', e.message); }
                                        return answers.length > 0 ? (
                                            <Typography variant="body2" ml={2} sx={{ color: 'success.main', fontWeight: 700 }}>
                                                Accepted: {answers.join(', ')}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" ml={2} sx={{ fontStyle: 'italic' }}>
                                                No answer set
                                            </Typography>
                                        );
                                    })()}

                                    {/* FRQ parts — recursive */}
                                    {['saq', 'dbq', 'leq'].includes(q.question_type) && (
                                        q.parts?.length > 0 ? (
                                            <Box ml={1}>
                                                <PreviewParts parts={q.parts} questionType={q.question_type} depth={0} colors={colors} isUpload={frqSubmissionMethod === 'upload'} />
                                            </Box>
                                        ) : frqSubmissionMethod === 'upload' ? (
                                            <Box sx={{ ml: 2, mt: 0.5, p: 0.8, border: `1px dashed ${colors.grey[500]}`, borderRadius: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography sx={{ fontSize: 12 }}>📎</Typography>
                                                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>Upload file...</Typography>
                                            </Box>
                                        ) : (
                                            <Box sx={{ ml: 2, mt: 0.5, p: 0.8, border: `1px dashed ${colors.grey[500]}`, borderRadius: 0.5 }}>
                                                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>{q.question_type === 'saq' ? 'Short answer...' : 'Essay response...'}</Typography>
                                            </Box>
                                        )
                                    )}
                                </Box>
                            ))}
                        </Box>
                    ))}

                    {sections.length === 0 && (
                        <Typography color="text.secondary" textAlign="center" py={4}>No sections or questions added yet.</Typography>
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmDialog ref={confirmRef} />

            <AIAutoFillModal
                open={aiModalOpen}
                onClose={() => setAiModalOpen(false)}
                onApply={handleAIApply}
                testFileUrl={fileSlots.test_pdf.url}
                answerKeyUrl={fileSlots.answer_key.url}
                api={api}
            />
            <ImportQuestionsModal
                open={importTarget !== null}
                onClose={() => setImportTarget(null)}
                onImport={handleImportQuestions}
                colors={colors}
                existingKeys={importTarget !== null
                    ? new Set(sections.flatMap((s) => (s.questions || []).map((q) => contentKey(q)).filter(Boolean)))
                    : undefined}
                targetLabel={importTarget === 'new'
                    ? 'New section'
                    : (typeof importTarget === 'number'
                        ? `Section ${importTarget + 1}${sections[importTarget]?.section_name && sections[importTarget].section_name !== `Section ${importTarget + 1}` ? ` — ${sections[importTarget].section_name}` : ''}`
                        : '')}
            />
            <GenerateFromChaptersModal
                open={generateOpen}
                onClose={() => setGenerateOpen(false)}
                onGenerate={handleGenerate}
                existingSections={sections.map(s => ({ name: s.section_name }))}
            />
            {/* One-time ask: which course do this test's chapter numbers belong to?
                Only appears when tagging a question in a test with no tagged
                questions to infer from; remembered for the rest of the session. */}
            <Dialog open={courseDialog !== null} onClose={() => setCourseDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Which course are these chapters from?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        A chapter number only means something within a course (Algebra 1's Ch 8 ≠ Geometry's Ch 8).
                        This test has no tagged questions yet, so pick the course once — it'll apply to the rest of your tagging here.
                    </Typography>
                    <Box display="flex" gap={2}>
                        <Autocomplete
                            freeSolo
                            size="small"
                            options={[...new Set(chapterCourses.map(c => c.course))]}
                            inputValue={dialogCourse}
                            onInputChange={(e, val) => {
                                setDialogCourse(val || '');
                                const match = chapterCourses.find(c => c.course === val);
                                if (match) setDialogKind(match.kind);
                            }}
                            renderInput={(params) => <TextField {...params} label="Course" placeholder="e.g. Algebra 1" autoFocus />}
                            sx={{ flex: 7 }}
                        />
                        <FormControl size="small" sx={{ flex: 3 }}>
                            <InputLabel>Numbering</InputLabel>
                            <Select value={dialogKind} label="Numbering" onChange={(e) => setDialogKind(e.target.value)}>
                                <MenuItem value="chapter">Chapter</MenuItem>
                                <MenuItem value="lesson">Lesson</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCourseDialog(null)}>Cancel</Button>
                    <Button variant="contained" color="secondary" disabled={!dialogCourse.trim()} onClick={confirmCourseDialog}>
                        Use this course
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
        </UnsavedChangesGuard>
    );
};

export default AddEditTest;
